import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import PageServices from '../../services/PageServices';
import CommonServices from '../../services/ComanServices';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import TimePicker from 'react-time-picker';
import OIPercentageGraph from './OIPercentageGraph';
import CalendarPicker from './CalendarPicker';
import OIChartsPanel from './OIChartsPanel';
import SpotChartModal, { SPOT_CHART_MODAL_ID } from './SpotChartModal';
import FuturePriceGraphModal from './FuturePriceGraphModal';
import useStickyTableHeader from './useStickyTableHeader';
import {
  rankHighestValues, pcrUnderStrike, swingLabel, applyDisplayStep, sliceAroundSpot,
  STRIKE_OPTIONS, buildReversal930, redStrikeFor, chartLevelsFor,
} from './optionChainShared';
import "./Time.css"

import { useSearchParams } from "react-router-dom";

// GFDL sends IV as a fraction (0.127 = 12.7%); show it as a percentage like other option chains.
// Values above 5 are assumed to be a percentage already.
const formatIV = (iv) => {
  const v = parseFloat(iv ?? 0) || 0;
  return (Math.abs(v) <= 5 ? v * 100 : v).toFixed(2);
};

const OTW_HEADER_SENTIMENT_EVENT = "otw-header-sentiment";

function emitHeaderSentiment(trading, tradingTwo) {
  window.dispatchEvent(
    new CustomEvent(OTW_HEADER_SENTIMENT_EVENT, { detail: { trading, tradingTwo } })
  );
}

/**
 * Same rules as Optionchain.fetchData after getOptionChain: `historical` for daily,
 * calculateEMA on historicalResponseEx.Result vs Result[0].Close for intraday.
 * Uses live option-chain API so the header matches the option chain page behavior.
 */
async function syncHeaderSentimentForSymbol(symbolToken) {
  if (!symbolToken) return;
  try {
    function calculateEMA(values, period = 3) {
      let ema = values[2]?.Low;
      const smoothingFactor = 2 / (period + 1);
      for (let i = 1; i >= 0; i--) {
        ema = smoothingFactor * values[i].Low + (1 - smoothingFactor) * ema;
      }
      return ema;
    }
    let response;
    if (
      symbolToken === "GOLD" ||
      symbolToken === "SILVER" ||
      symbolToken === "CRUDEOIL" ||
      symbolToken === "COPPER" ||
      symbolToken === "NATURALGAS"
    ) {
      response = await PageServices.getComodityOptionChain({
        symbolToken,
      });
    } else {
      response = await PageServices.getOptionChain({
        symbolToken,
      });
      if (Array.isArray(response?.formattedData)) {
        response.formattedData.sort((x, y) => parseFloat(x.strikePrice) - parseFloat(y.strikePrice));
      }
    }
    const emaLow = calculateEMA(response?.historicalResponseEx?.Result);
    let tradingTwo;
    if (response?.historicalResponseEx?.Result[0].Close > emaLow) {
      tradingTwo = "Bullish";
    } else {
      tradingTwo = "Bearish";
    }
    emitHeaderSentiment(response?.historical, tradingTwo);
  } catch (err) {
    console.error("syncHeaderSentimentForSymbol:", err);
  }
}

function BackTester() {
  const [queryParams] = useSearchParams();
  const queryDate = queryParams.get("date");
  const queryTime = queryParams.get("time");
  const querySymbol = queryParams.get("symbolToken");
  // rawData: the chain as fetched for the selected date / time. `data` (below) is what the
  // table shows: GOLD / SILVER every 1000 strike, and the "Select Strike" range around spot.
  const [rawData, setData] = useState([]);
  const [dates, setDates] = useState([]);

  const [expiryDate, setExpiryDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [expiryDates, setExpiryDates] = useState([]);
  const [selectDates, setSelectDates] = useState([]);
  const [selectedDateForStock, setSelectedDateForStock] = useState('')
  const [selectedExpiryDate, setSelectedExpiryDate] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  // const [timePicked, setTimePicked] = useState();
  const [selectedTime, setSelectedTime] = useState();
  const [showClock, setShowClock] = useState(false);
  const [processedData, setProcessedData] = useState([]);
  const [selectedStrikePrice, setSelectedStrikePrice] = useState(null);
  const [greekChainData, setGreekChainData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [spotPrice, setSpotPrice] = useState(0);
  const [spotChange, setSpotChange] = useState(null); // spot day change at the selected time
  const [strikeDropdownOpen, setStrikeDropdownOpen] = useState(false);
  const tableBoxRef = useRef(null);
  const headerRow1Ref = useRef(null);
  useStickyTableHeader(tableBoxRef, headerRow1Ref, 64); // 64 = playback bar
  // Probable Swing High / Low columns (Settings → showColoum). Shown unless switched off, and
  // updated as soon as the setting changes (the table did not redraw on the toggle before).
  const [showSwing, setShowSwing] = useState(() => localStorage.getItem('showColoum') !== 'false');
  useEffect(() => {
    const sync = () => setShowSwing(localStorage.getItem('showColoum') !== 'false');
    window.addEventListener('otw:setting', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('otw:setting', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const [selectedStrike, setSelectedStrike] = useState('');
  // Date / time / symbol of the chain on screen; the charts and popups follow it.
  const [appliedQuery, setAppliedQuery] = useState(null);
  const [latestFuture, setLatestFuture] = useState(null);
  const [oiChartsInstrument, setOiChartsInstrument] = useState(null);
  const data = useMemo(
    () => sliceAroundSpot(applyDisplayStep(rawData, appliedQuery?.symbol || localStorage.getItem('stock')), spotPrice, selectedStrike),
    [rawData, appliedQuery, spotPrice, selectedStrike]
  );
  const [highestCEOIChange, setHighestCEOIChange] = useState(0);
  const [secondHighestCEOIChange, setSecondHighestCEOIChange] = useState(0);
  const [thirdHighestCEOIChange, setThirdHighestCEOIChange] = useState(0);
  const [highestCEOI, setHighestCEOI] = useState(0);
  const [secondHighestCEOI, setSecondHighestCEOI] = useState(0);
  const [thirdHighestCEOI, setThirdHighestCEOI] = useState(0);
  const [highestCEVolume, setHighestCEVolume] = useState(0);
  const [secondHighestCEVolume, setSecondHighestCEVolume] = useState(0);
  const [thirdHighestCEVolume, setThirdHighestCEVolume] = useState(0);
  const [highestPEOIChange, setHighestPEOIChange] = useState(0);
  const [secondHighestPEOIChange, setSecondHighestPEOIChange] = useState(0);
  const [thirdHighestPEOIChange, setThirdHighestPEOIChange] = useState(0);
  const [highestPEOI, setHighestPEOI] = useState(0);
  const [secondHighestPEOI, setSecondHighestPEOI] = useState(0);
  const [thirdHighestPEOI, setThirdHighestPEOI] = useState(0);
  const [highestPEVolume, setHighestPEVolume] = useState(0);
  const [secondHighestPEVolume, setSecondHighestPEVolume] = useState(0);
  const [thirdHighestPEVolume, setThirdHighestPEVolume] = useState(0);
  const [resistanceLevel, setResistanceLevel] = useState(null);
  const [supportLevel, setSupportLevel] = useState(null);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [oiPercentageGraphPE, setOiPercentageGraphPE] = useState('');
  const [oiPercentageGraphCE, setOiPercentageGraphCE] = useState('');

  // Percentage graph (AOC-style % of the strongest strike) for the clicked strike
  const [oiPctGraph, setOiPctGraph] = useState({ points: [] });
  const [oiPctGraphLoading, setOiPctGraphLoading] = useState(false);
  const oiPctRequestRef = useRef(0);
  const [support, setSupport] = useState(0);
  const [resistance, setResistance] = useState(0);
  const [support2, setSupport2] = useState(0);
  const [resistance2, setResistance2] = useState(0);
  const [date, setDate] = useState(0);

  const [firstOptionChain, setFirstOptionChain] = useState([]);
  const { isLoggedIn, logout } = useContext(AuthContext);
  const [selectedTimePicked, setSelectedTimePicked] = useState({ hour: 9, minute: 15, period: "AM" });
  const [clockHandAngles, setClockHandAngles] = useState({ hour: 0, minute: 0 });
  const [isHourPickerVisible, setIsHourPickerVisible] = useState(false);
  const [isMinutePickerVisible, setIsMinutePickerVisible] = useState(false);
  // const [showTimePicker, setShowTimePicker] = useState(false);
  // const [showTimePicker, setShowTimePicker] = useState(false);
  // time picker code 
  // console.log("selectedDateForStock",selectedDateForStock)
  function convertDateFormat(dateStr) {
    const months = {
      Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
      Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
    };

    const [day, month, year] = dateStr.split("-");
    const formattedDate = `${year}-${months[month]}-${day.padStart(2, "0")}`;

    return formattedDate;
  }
  const styles = {
    dropdown: {
      position: 'relative',
      display: 'inline-block',
    },
    dropbtn: {
      backgroundColor: '#f77c22',
      color: 'white',
      padding: '8px 16px',
      fontSize: '16px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      marginBottom: '10px',
    },
    dropdownContent: {
      position: 'absolute',
      backgroundColor: '#f77c22',
      minWidth: '160px',
      boxShadow: '0px 8px 16px rgba(0,0,0,0.2)',
      borderRadius: '8px',
      overflow: 'hidden',
      zIndex: 1,
      marginTop: '5px',
    },
    dropdownItem: {
      color: 'white',
      padding: '12px 16px',
      textDecoration: 'none',
      display: 'block',
      textAlign: 'center',
      fontWeight: 'bold',
      borderBottom: '1px solid white',
      cursor: 'pointer',
    },
    lastItem: {
      borderBottom: 'none',
    },
    hoverItem: {
      backgroundColor: '#e46c15',
    }
  };
  const [showTable, setShowTable] = useState(false);
  const [weeklyUpper, setWeeklyUpper] = useState(0);
  const [weeklyDown, setWeeklyDown] = useState(0);
  const [activeButtons, setActiveButtons] = useState([]);
  const [day, setDay] = useState(null);
  const [playbackInterval, setPlaybackInterval] = useState(2000);
  // const [selectedTime, setSelectedTime] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  let timerRef = useRef(null);
  const timeRef = useRef(null); // latest selected time (the playback timer reads it)
  const fetchDataRef = useRef(null);

  // helper - time change
  const changeTime = (timeStr, intervalMs, direction) => {
    // No time chosen yet: start from the market open.
    let parts = String(timeStr || "09:15:00").split(":").map(Number);
    let date = new Date();
    if (parts.length === 2) {
      date.setHours(parts[0], parts[1], 0, 0);
    } else {
      date.setHours(parts[0], parts[1], parts[2], 0);
    }

    let newTime =
      direction === "forward"
        ? date.getTime() + intervalMs
        : date.getTime() - intervalMs;

    let newDate = new Date(newTime);
    let hh = String(newDate.getHours()).padStart(2, "0");
    let mm = String(newDate.getMinutes()).padStart(2, "0");
    let ss = String(newDate.getSeconds()).padStart(2, "0");

    return `${hh}:${mm}:${ss}`;
  };

  // interval button click
  const handleIntervalChange = (intervalMs, direction) => {
    setPlaybackInterval(intervalMs);

    // A manual step pauses playback, then loads the new time once.
    stopPlayback();
    const newTime = changeTime(timeRef.current || selectedTime, intervalMs, direction);
    timeRef.current = newTime;
    setSelectedTime(newTime);
    fetchData(newTime);
  };

  // Playback: every PLAY_TICK_MS the time moves the selected step (1 / 3 / 5 min) forward or backward and that
  // minute's chain is fetched. Stops at the session edges (09:15 / 15:30).
  const PLAY_TICK_MS = 2000;
  // Playback step = the interval chosen on the right (10 Sec / 30 Sec / 1 Min / 3 Min), default 1 Min.
  const [playStepMs, setPlayStepMs] = useState(60 * 1000);
  const playStepRef = useRef(60 * 1000);
  playStepRef.current = playStepMs;
  // Which side shows the selected interval: 'forward' (right) or 'backward' (left).
  const [stepSide, setStepSide] = useState('forward');
  const stepLabel = (ms) => (ms < 60000 ? `${ms / 1000} sec` : `${ms / 60000} min`);
  const [playDirection, setPlayDirection] = useState(null); // 'forward' | 'backward' | null
  const stopPlayback = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    setIsPlaying(false);
    setPlayDirection(null);
  };
  const startPlayback = (direction) => {
    if (playDirection === direction) { stopPlayback(); return; } // same button again = pause
    clearInterval(timerRef.current);
    if (!timeRef.current) timeRef.current = selectedTime || '09:15:00';
    timerRef.current = setInterval(() => {
      const next = changeTime(timeRef.current, playStepRef.current, direction);
      if (next < '09:15:00' || next > '15:30:59') { stopPlayback(); return; }
      timeRef.current = next;
      setSelectedTime(next);
      fetchDataRef.current?.(next);
    }, PLAY_TICK_MS);
    setIsPlaying(true);
    setPlayDirection(direction);
    setStepSide(direction); // the selected interval moves to this side
  };
  // Right-side interval: becomes the playback step for both directions (stays highlighted).
  // While playing it only changes the step; otherwise it also moves forward once, as before.
  const selectPlayStep = (ms, direction = 'forward') => {
    setPlayStepMs(ms);
    setStepSide(direction);
    if (!isPlaying) handleIntervalChange(ms, direction);
  };
  // centre button: pause when playing, else play forward
  const togglePlayback = () => (isPlaying ? stopPlayback() : startPlayback('forward'));
  useEffect(() => () => clearInterval(timerRef.current), []);

const toggleButton = (buttonName) => {
    if (activeButtons.includes(buttonName)) {
      setActiveButtons(activeButtons.filter(name => name !== buttonName)); // remove button
    } else {
      setActiveButtons([...activeButtons, buttonName]); // add button
    }
  };

  const toggleDropdown = () => {
    setOpen(!open);
  };
  const [open, setOpen] = useState(false);
  const [selectedGreeks, setSelectedGreeks] = useState([]);
  const greekitems = ['GAMMA', 'VEGA', 'THETA', 'DELTA', 'RESET'];
  const handleGreekSelect = (item) => {
    if (item === 'RESET') {
      setSelectedGreeks([]); // clear all on reset
    } else if (!selectedGreeks.includes(item)) {
      setSelectedGreeks(prev => [...prev, item]); // add only if not already selected
    }
    toggleDropdown();
  };
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [rangeOptions, setRangeOptions] = useState([]);
  const [callOI, setCallOI] = useState(0);
  const [putOI, setPutOI] = useState(0);
  const [callOIChange, setCallOIChange] = useState(0);
  const [putOIChange, setPutOIChange] = useState(0);
  const [dataCallPutOIChange, setDataCallPutOIChange] = useState([]);
  const [dataCallPutOI, setDataCallPutOI] = useState([]);
  // new vega feature
  const [currentVegaCall, setCurrentVegaCall] = useState([]);
  const [currentStrikeCountCall, setCurrentStrikeCountCall] = useState([]);
  const [oldStrikeCountCall, setOldStrikeCountCall] = useState(0);
  const [oldVegaCall, setOldVegaCall] = useState(0);
  const [vegaCallDiff, setVegaCallDiff] = useState(0);
  const [strikeCountCallDiff, setStrikeCountCallDiff] = useState(0);
  const [currentVegaPut, setCurrentVegaPut] = useState([]);
  const [currentStrikeCountPut, setCurrentStrikeCountPut] = useState([]);
  const [oldStrikeCountPut, setOldStrikeCountPut] = useState(0);
  const [oldVegaPut, setOldVegaPut] = useState(0);
  const [vegaPutDiff, setVegaPutDiff] = useState(0);
  const [strikeCountPutDiff, setStrikeCountPutDiff] = useState(0);
  const [currentVegaPutDiff, setCurrentVegaPutDiff] = useState(0);
  const [currentStrikeCountPutDiff, setCurrentStrikeCountPutDiff] = useState(0);
  const [oldVegaPutDiff, setOldVegaPutDiff] = useState(0);
  const [oldStrikeCountPutDiff, setOldStrikeCountPutDiff] = useState(0);
  const [vegaCallAndPutDiff, setVegaCallAndPutDiff] = useState(0);
  const [strikeCountCallAndPutDiff, setStrikeCountCallAndPutDiff] = useState(0);
  const [currentGammaCall, setCurrentGammaCall] = useState(0);
  const [currentDeltaCall, setCurrentDeltaCall] = useState(0);
  const [currentThetaCall, setCurrentThetaCall] = useState(0);
  const [currentGammaPut, setCurrentGammaPut] = useState(0);
  const [currentDeltaPut, setCurrentDeltaPut] = useState(0);
  const [currentThetaPut, setCurrentThetaPut] = useState(0);
  const [oldGammaCall, setOldGammaCall] = useState(0);
  const [oldDeltaCall, setOldDeltaCall] = useState(0);
  const [oldThetaCall, setOldThetaCall] = useState(0);
  const [oldGammaPut, setOldGammaPut] = useState(0);
  const [oldDeltaPut, setOldDeltaPut] = useState(0);
  const [oldThetaPut, setOldThetaPut] = useState(0);
  const [gammaCallDiff, setGammaCallDiff] = useState(0);
  const [deltaCallDiff, setDeltaCallDiff] = useState(0);
  const [thetaCallDiff, setThetaCallDiff] = useState(0);

  const [gammaPutDiff, setGammaPutDiff] = useState(0);
  const [deltaPutDiff, setDeltaPutDiff] = useState(0);
  const [thetaPutDiff, setThetaPutDiff] = useState(0);

  const [currentGammaPutDiff, setCurrentGammaPutDiff] = useState(0);
  const [currentDeltaPutDiff, setCurrentDeltaPutDiff] = useState(0);
  const [currentThetaPutDiff, setCurrentThetaPutDiff] = useState(0);
  // OLD: PUT - CALL
  const [oldGammaPutDiff, setOldGammaPutDiff] = useState(0);
  const [oldDeltaPutDiff, setOldDeltaPutDiff] = useState(0);
  const [oldThetaPutDiff, setOldThetaPutDiff] = useState(0);

  // DIFF: PUT - CALL (current vs old diffs)
  const [gammaCallAndPutDiff, setGammaCallAndPutDiff] = useState(0);
  const [deltaCallAndPutDiff, setDeltaCallAndPutDiff] = useState(0);
  const [thetaCallAndPutDiff, setThetaCallAndPutDiff] = useState(0);
  const [showGraph, setShowGraph] = useState(false);

  useEffect(() => {
    updateClockHands();
  }, [selectedTimePicked]);

  const updateClockHands = () => {
    let displayHour = selectedTimePicked.hour;
    if (selectedTimePicked.period === "PM" && selectedTimePicked.hour !== 12) {
      displayHour += 12;
    } else if (selectedTimePicked.period === "AM" && selectedTimePicked.hour === 12) {
      displayHour = 0;
    }

    const hourAngle = ((displayHour % 12) * 30) + (selectedTimePicked.minute / 60) * 30;
    const minuteAngle = (selectedTimePicked.minute / 60) * 360;

    setClockHandAngles({ hour: hourAngle, minute: minuteAngle });
  };

  const handleHourClick = () => {
    setIsHourPickerVisible(!isHourPickerVisible);
    setIsMinutePickerVisible(false);
  };

  const handleMinuteClick = () => {
    setIsMinutePickerVisible(!isMinutePickerVisible);
    setIsHourPickerVisible(false);
  };

  const handleNumberClick = (number) => {
    setSelectedTimePicked((prevTime) => ({ ...prevTime, hour: number }));
    setIsHourPickerVisible(false);
  };

  const handleMinuteSelection = (minute) => {
    setSelectedTimePicked((prevTime) => ({ ...prevTime, minute }));
    setIsMinutePickerVisible(false);
  };

  const togglePeriod = (newPeriod) => {
    setSelectedTimePicked((prevTime) => ({ ...prevTime, period: newPeriod }));
  };


  // console.log("selectedTimePicked",selectedTimePicked)
  // end code
  React.useEffect(() => {
    if (queryDate && queryTime && querySymbol) {
      // Opened from a link (e.g. Power Stocks): fill the date / time so Play and the
      // 10 Sec … 3 Min buttons continue from that moment.
      setSelectedDateForStock(queryDate);
      setSelectedTime(queryTime);
      timeRef.current = queryTime;
      fetchHistoricalData();
    }
  }, [queryDate, queryTime, querySymbol]);
  // const fetchHistoricalData = async () => {
  //   try {
  //     const response = await PageServices.getOldData({
  //       date: queryDate,
  //       time: queryTime,
  //       symbolToken: querySymbol,
  //     });
  //     console.log("Historical Data Response:", response);
  //       setData(response?.data); 
  //   } catch (error) {
  //     console.error("Error fetching historical data:", error);
  //   }
  // };
  const fetchHistoricalData = async () => {


    // console.log(">>>",selectedExpiryDate,selectedDateForStock,selectedTime,localStorage.getItem('stock'))
    try {
      const response = await PageServices.getOldData({
        date: queryDate,
        time: queryTime,
        symbolToken: querySymbol,
      });
      console.log("Historical Data Response:", response);

      if (response && response.data) {

        const spot = await PageServices.getOldSpot({
          date: queryDate, // You can make this dynamic as needed
          time: queryTime,       // You can make this dynamic as needed
          symbolToken: querySymbol,
        })

        if (spot && spot.data) {
          // console.log("spot",spot)
          setSpotPrice(spot?.data?.spotPrice?.LastTradePrice);
          setSpotChange(spot?.data?.spotPrice?.PriceChange ?? null);
        }
        PageServices.getDataAverageSuportandRessitans(localStorage.getItem("stock"), convertDateFormat(selectedDateForStock)).then((response) => {
          setFirstOptionChain(response);
          console.log(">>>>", response);
        }).catch((error) => {
          console.error('Error fetching data for support and ressitance:', error);
        });
        const options = response.data; // Extract the options array
        // setSpotPrice(24945);
        setData(options);
        setAppliedQuery({ date: queryDate, time: queryTime, symbol: querySymbol });

        // console.log(response?.data?.spotPrice?.LastTradePrice)

        // Find the highest and second-highest values for each column
        const ceOpenInterestChanges = [...new Set(options.map(item => item.CE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const ceOpenInterests = [...new Set(options.map(item => item.CE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const ceTotalQtyTraded = [...new Set(options.map(item => item.CE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);
        const peOpenInterestChanges = [...new Set(options.map(item => item.PE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const peOpenInterests = [...new Set(options.map(item => item.PE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const peTotalQtyTraded = [...new Set(options.map(item => item.PE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);

        const setValues = (values, setHighest, setSecond, setThird) => {
          setHighest(values[0] || 0);
          setSecond(values[1] || 0);
          setThird(values[2] || 0);
        };

        setValues(ceOpenInterestChanges, setHighestCEOIChange, setSecondHighestCEOIChange, setThirdHighestCEOIChange);
        setValues(ceOpenInterests, setHighestCEOI, setSecondHighestCEOI, setThirdHighestCEOI);
        setValues(ceTotalQtyTraded, setHighestCEVolume, setSecondHighestCEVolume, setThirdHighestCEVolume);
        setValues(peOpenInterestChanges, setHighestPEOIChange, setSecondHighestPEOIChange, setThirdHighestPEOIChange);
        setValues(peOpenInterests, setHighestPEOI, setSecondHighestPEOI, setThirdHighestPEOI);
        setValues(peTotalQtyTraded, setHighestPEVolume, setSecondHighestPEVolume, setThirdHighestPEVolume);

        const maxCEOI = Math.max(...options.map(item => item.CE?.OpenInterest || 0));
        const maxPEOI = Math.max(...options.map(item => item.PE?.OpenInterest || 0));
        const resistance = options.find(item => item.CE?.OpenInterest === maxCEOI)?.strikePrice;
        const support = options.find(item => item.PE?.OpenInterest === maxPEOI)?.strikePrice;

        setResistanceLevel(resistance);
        setSupportLevel(support);
        syncHeaderSentimentForSymbol(querySymbol || localStorage.getItem("stock"));
      } else {
        setData([]); // Handle case where options are not present
      }
    } catch (error) {
      console.error('Error fetching data:', error.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchData = async (timeOverride) => {
    // The Fetch button passes a click event; playback passes the time to load.
    const time = typeof timeOverride === 'string' ? timeOverride : selectedTime;
    const symbolToken = localStorage.getItem('stock');

    // Expiry is optional: without one the API uses the nearest expiry on that date (as for links).
    if (!selectedDateForStock || !time) {
      console.error("Please select expiry date, date, and time before fetching data.");
      setLoading(false);
      return;

    }
    // console.log(">>>",selectedExpiryDate,selectedDateForStock,selectedTime,localStorage.getItem('stock'))
    try {
      const response = await PageServices.getOldData({
        date: selectedDateForStock, // You can make this dynamic as needed
        time,       // You can make this dynamic as needed
        symbolToken,  // Adjust according to your requirement
        expiryDate: selectedExpiryDate,
      });

      if (response && response.data) {

        const spot = await PageServices.getOldSpot({
          date: selectedDateForStock, // You can make this dynamic as needed
          time,       // You can make this dynamic as needed
          symbolToken,
        })

        if (spot && spot.data) {
          // console.log("spot",spot)
          setSpotPrice(spot?.data?.spotPrice?.LastTradePrice);
          setSpotChange(spot?.data?.spotPrice?.PriceChange ?? null);
        }
        PageServices.getDataAverageSuportandRessitans(localStorage.getItem("stock"), convertDateFormat(selectedDateForStock)).then((response) => {
          setFirstOptionChain(response);
          console.log(">>>>", response);
        }).catch((error) => {
          console.error('Error fetching data for support and ressitance:', error);
        });
        const options = response.data; // Extract the options array
        // setSpotPrice(24945);
        setData(options);
        setAppliedQuery({ date: selectedDateForStock, time, symbol: symbolToken, expiry: selectedExpiryDate });

        // console.log(response?.data?.spotPrice?.LastTradePrice)

        // Find the highest and second-highest values for each column
        const ceOpenInterestChanges = [...new Set(options.map(item => item.CE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const ceOpenInterests = [...new Set(options.map(item => item.CE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const ceTotalQtyTraded = [...new Set(options.map(item => item.CE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);
        const peOpenInterestChanges = [...new Set(options.map(item => item.PE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
        const peOpenInterests = [...new Set(options.map(item => item.PE?.OpenInterest ?? 0))].sort((a, b) => b - a);
        const peTotalQtyTraded = [...new Set(options.map(item => item.PE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);

        const setValues = (values, setHighest, setSecond, setThird) => {
          setHighest(values[0] || 0);
          setSecond(values[1] || 0);
          setThird(values[2] || 0);
        };

        setValues(ceOpenInterestChanges, setHighestCEOIChange, setSecondHighestCEOIChange, setThirdHighestCEOIChange);
        setValues(ceOpenInterests, setHighestCEOI, setSecondHighestCEOI, setThirdHighestCEOI);
        setValues(ceTotalQtyTraded, setHighestCEVolume, setSecondHighestCEVolume, setThirdHighestCEVolume);
        setValues(peOpenInterestChanges, setHighestPEOIChange, setSecondHighestPEOIChange, setThirdHighestPEOIChange);
        setValues(peOpenInterests, setHighestPEOI, setSecondHighestPEOI, setThirdHighestPEOI);
        setValues(peTotalQtyTraded, setHighestPEVolume, setSecondHighestPEVolume, setThirdHighestPEVolume);

        const maxCEOI = Math.max(...options.map(item => item.CE?.OpenInterest || 0));
        const maxPEOI = Math.max(...options.map(item => item.PE?.OpenInterest || 0));
        const resistance = options.find(item => item.CE?.OpenInterest === maxCEOI)?.strikePrice;
        const support = options.find(item => item.PE?.OpenInterest === maxPEOI)?.strikePrice;

        setResistanceLevel(resistance);
        setSupportLevel(support);
        syncHeaderSentimentForSymbol(localStorage.getItem("stock"));
      } else {
        setData([]); // Handle case where options are not present
      }
    } catch (error) {
      console.error('Error fetching data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  fetchDataRef.current = fetchData;
  timeRef.current = selectedTime;

  // Columns on each side of the strike column: OI Chg, OI, Volume, LTP + optional swing,
  // IV, IV/&/Y and Greeks columns.
  const sideColumns = 4 + (showSwing ? 1 : 0)
    + (activeButtons.includes('IV') ? 1 : 0) + (activeButtons.includes('IV/&/Y') ? 1 : 0)
    + ['GAMMA', 'VEGA', 'THETA', 'DELTA'].filter((g) => selectedGreeks.includes(g)).length;

  // Highest / 2nd / 3rd values of the shown rows, AOC rule by spot: resistance (CE) from 2
  // strikes below spot upward, support (PE) from 2 strikes above spot downward.
  useEffect(() => {
    if (!data.length) return;
    const r = rankHighestValues(data, spotPrice);
    const set3 = ([a, b, c], s1, s2, s3) => { s1(a); s2(b); s3(c); };
    set3(r.ceOIChange, setHighestCEOIChange, setSecondHighestCEOIChange, setThirdHighestCEOIChange);
    set3(r.ceOI, setHighestCEOI, setSecondHighestCEOI, setThirdHighestCEOI);
    set3(r.ceVolume, setHighestCEVolume, setSecondHighestCEVolume, setThirdHighestCEVolume);
    set3(r.peOIChange, setHighestPEOIChange, setSecondHighestPEOIChange, setThirdHighestPEOIChange);
    set3(r.peOI, setHighestPEOI, setSecondHighestPEOI, setThirdHighestPEOI);
    set3(r.peVolume, setHighestPEVolume, setSecondHighestPEVolume, setThirdHighestPEVolume);
  }, [data, spotPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDatafirstOptionChain = async () => {
    console.log(localStorage.getItem("stock"));
    PageServices.getDataAverageSuportandRessitans(localStorage.getItem("stock"), selectedDateForStock).then((response) => {
      setFirstOptionChain(response);
      console.log(">>>>", response);
    }).catch((error) => {
      console.error('Error fetching data for support and ressitance:', error);
    });
  }

  const processOptionChainData = (response) => {
    const ceOpenInterestChanges = [...new Set(response.map(item => item.CE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
    const ceOpenInterests = [...new Set(response.map(item => item.CE?.OpenInterest ?? 0))].sort((a, b) => b - a);
    const ceTotalQtyTraded = [...new Set(response.map(item => item.CE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);
    const peOpenInterestChanges = [...new Set(response.map(item => item.PE?.OpenInterestChange ?? 0))].sort((a, b) => b - a);
    const peOpenInterests = [...new Set(response.map(item => item.PE?.OpenInterest ?? 0))].sort((a, b) => b - a);
    const peTotalQtyTraded = [...new Set(response.map(item => item.PE?.TotalQtyTraded ?? 0))].sort((a, b) => b - a);

    const setValues = (values, setHighest, setSecond, setThird) => {
      setHighest(values[0] || 0);
      setSecond(values[1] || 0);
      setThird(values[2] || 0);
    };

    setValues(ceOpenInterestChanges, setHighestCEOIChange, setSecondHighestCEOIChange, setThirdHighestCEOIChange);
    setValues(ceOpenInterests, setHighestCEOI, setSecondHighestCEOI, setThirdHighestCEOI);
    setValues(ceTotalQtyTraded, setHighestCEVolume, setSecondHighestCEVolume, setThirdHighestCEVolume);
    setValues(peOpenInterestChanges, setHighestPEOIChange, setSecondHighestPEOIChange, setThirdHighestPEOIChange);
    setValues(peOpenInterests, setHighestPEOI, setSecondHighestPEOI, setThirdHighestPEOI);
    setValues(peTotalQtyTraded, setHighestPEVolume, setSecondHighestPEVolume, setThirdHighestPEVolume);

    const maxCEOI = Math.max(...response.map(item => item.CE?.OpenInterest || 0));
    const maxPEOI = Math.max(...response.map(item => item.PE?.OpenInterest || 0));
    const resistance = response.find(item => item.CE?.OpenInterest === maxCEOI)?.strikePrice;
    const support = response.find(item => item.PE?.OpenInterest === maxPEOI)?.strikePrice;
    setResistanceLevel(resistance);
    setSupportLevel(support);
  };

  // No `time`: the graph always covers the full session of the selected date.
  // Responses from an older click are ignored.
  const loadOiPctGraph = (instrumentIdentifier) => {
    const requestId = ++oiPctRequestRef.current;
    setOiPctGraphLoading(true);
    setOiPctGraph({ points: [] });
    PageServices.getOIPercentageGraph({
      instrumentIdentifier,
      date: selectedDateForStock,
      symbolToken: localStorage.getItem("stock"),
    }).then((response) => {
      if (requestId === oiPctRequestRef.current) setOiPctGraph(response?.data || { points: [] });
    }).catch((err) => {
      console.error("Error fetching OI % graph:", err);
    }).finally(() => {
      if (requestId === oiPctRequestRef.current) setOiPctGraphLoading(false);
    });
  };

  const onHistoricsClick = (data) => {
    const identifier = data?.InstrumentIdentifier || "";
    const parts = identifier.split("_");
    if (parts.length >= 5) {
      const optionType = parts[3];
      const strikePrice = parts[4];
      if (optionType === "PE") {
        setOiPercentageGraphPE(`PE | ${strikePrice}`);
        setOiPercentageGraphCE("");
      } else if (optionType === "CE") {
        setOiPercentageGraphCE(`CE | ${strikePrice}`);
        setOiPercentageGraphPE("");
      }
    }

    loadOiPctGraph(data.InstrumentIdentifier);

    PageServices.getHistorydata({
      instrumentIdentifier: data.InstrumentIdentifier,
      date: selectedDateForStock,
      // no time: the graph covers the whole session (09:15–15:30) of the selected date
      symbolToken: localStorage.getItem("stock"),
    }).then((response) => {
      const processData = (rawData) => {
        if (!rawData?.length) return [];
        const marketOpenMinutes = 9 * 60 + 15;
        const marketCloseMinutes = 15 * 60 + 30;

        const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
        let selectedYear, selectedMonth, selectedDay;
        if (selectedDateForStock) {
          const parts = selectedDateForStock.split("-");
          if (parts.length >= 3) {
            if (parts[0].length === 4) {
              selectedYear = parseInt(parts[0], 10);
              selectedMonth = parseInt(parts[1], 10) - 1;
              selectedDay = parseInt(parts[2], 10);
            } else {
              selectedDay = parseInt(parts[0], 10);
              selectedMonth = months[parts[1]];
              selectedYear = parseInt(parts[2], 10);
            }
          }
        }

        return rawData
          .map((entry) => {
            const dateObj = new Date(entry.LastTradeTime * 1000);
            return {
              ...entry,
              LastTradeTime: dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              _timestamp: dateObj.getTime(),
            };
          })
          .filter((entry) => {
            const d = new Date(entry._timestamp);
            const minutesFromMidnight = d.getHours() * 60 + d.getMinutes();
            const withinMarketHours = minutesFromMidnight >= marketOpenMinutes && minutesFromMidnight <= marketCloseMinutes;

            const matchesSelectedDate =
              selectedYear != null &&
              d.getFullYear() === selectedYear &&
              d.getMonth() === selectedMonth &&
              d.getDate() === selectedDay;

            return withinMarketHours && (selectedYear == null || matchesSelectedDate);
          })
          .sort((a, b) => a._timestamp - b._timestamp);
      };
      setProcessedData(processData(response.data));
    }).catch((err) => console.error("Error fetching percentage graph:", err));
  };



  const onHistoricsMultipleClick = (data) => {
    // console.log(data);
    PageServices.getHistorydataOichg({
      instrumentIdentifier: data.CE.InstrumentIdentifier,
      date: selectedDateForStock,
      // no time: the graph covers the whole session (09:15–15:30) of the selected date
      symbolToken: localStorage.getItem("stock"),
    })
      .then((response) => {
        console.log(response);

        const processChartData = (data) => {
          const ceData = data.data.ce;
          const peData = data.data.pe;

          return ceData.map((ceItem, index) => {
            const peItem = peData[index];
            return {
              time: new Date(ceItem.LastTradeTime * 1000).toLocaleTimeString(),
              ceOI: ceItem.OpenInterest,
              peOI: peItem.OpenInterest,
              oiDifference:
                peItem.OpenInterest - ceItem.OpenInterest,
              ceOIChange: ceItem.OpenInterestChange,
              peOIChange: peItem.OpenInterestChange,
              oiChgDifference: peItem.OpenInterestChange - ceItem.OpenInterestChange,
              ceVolume: ceItem.TotalQtyTraded,
              peVolume: peItem.TotalQtyTraded,
              volumeDifference: peItem.TotalQtyTraded - ceItem.TotalQtyTraded,
              volumedevide: ceItem.TotalQtyTraded / peItem.TotalQtyTraded,
              ceLtp: ceItem.LastTradePrice,
              peLtp: peItem.LastTradePrice,
              pcrOi: peItem.OpenInterest / ceItem.OpenInterest,
              pcrOiChange: peItem.OpenInterestChange / ceItem.OpenInterestChange
            };
          });
        };
        setChartData(processChartData(response));
      })
      .catch((error) => {
        console.log(error);
      });
  };


  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const adjustedHours = hours % 12 || 12;
    return `${adjustedHours}:${minutes} ${suffix}`;
  };

  const processGreekChainData = (data) => {
    return data.map((entry) => ({
      time: formatTime(entry.time),
      OI: entry.OI,
      OIChange: entry.OIChange,
      Volume: entry.Volume,
    }));
  };

  const OptionChainChart = ({ data }) => (
    <LineChart
      width={950}
      height={500}
      data={processGreekChainData(data)}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="time" />
      <YAxis />
      <Tooltip />
      <Legend />
      <Line type="monotone" dataKey="OI" stroke="#0000FF" dot={false} />
      <Line type="monotone" dataKey="OIChange" stroke="#00FF00" dot={false} />
      <Line type="monotone" dataKey="Volume" stroke="#FF0000" dot={false} />
    </LineChart>
  );
  useEffect(() => {
    calculateOpenInterestChange();
  }, [rangeStart, rangeEnd, data]);
  // const onModalOpen = (instrument) => {
  //   fetchGreekChainData(instrument);
  // };

  const getSpotPriceRow = () => {
    if (spotPrice) {
      const nearestStrikePrice = data.find(item => item.strikePrice >= spotPrice) || data[data.length + 1];
      const index = data.indexOf(nearestStrikePrice);

      return (
        <tr key="spot-price">
          {/* One cell across the whole row (whatever IV / Greeks columns are shown), so the
              banner is never clipped and never widens the strike column; one line, centred. */}
          <td colSpan={100} style={{ padding: '8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                data-bs-toggle="modal"
                data-bs-target="#futurpricegraph"
                title="Future Price Graph"
                style={{ background: '#004894', color: '#fff', borderRadius: '35px', padding: '8px 30px', whiteSpace: 'nowrap' }}
              >
                F : {latestFuture?.futurePrice || 0} * Spot : {spotPrice}
                {spotChange != null && ` (${spotChange > 0 ? '+' : ''}${Number(spotChange).toFixed(2)})`}
                {' '}* F-S : {latestFuture?.futurePrice ? (latestFuture.futurePrice - spotPrice).toFixed(1) : '0.0'}
              </button>
            </div>
          </td>
        </tr>
      );
    }
    return null;
  };

  const getNearestValues = () => {
    const nearestIndex = data.findIndex(item => item.strikePrice >= spotPrice);
    const start = Math.max(0, nearestIndex - 2);
    const end = Math.min(data.length, nearestIndex + 3);
    return data.slice(start, end);
  };

  const formatNumber = (value) => {
    const lotSize        = Number(localStorage.getItem('expiry')) || 1;
    const showDataInLots = localStorage.getItem('showDataInLots') === 'true';
    const showQt         = localStorage.getItem('showQt')         === 'true';
    const showNumbers    = localStorage.getItem('showNumbers')    === 'true';

    // Divide by lot size when either lots toggle is enabled
    const useValue = (showDataInLots || showQt) ? value / lotSize : value;

    // showNumbers: return raw divided value without K/L formatting
    if (showNumbers) return useValue;

    // Format with K / L / Cr suffix (applied to the divided value)
    const abs = Math.abs(useValue);
    if (abs >= 10000000) return `${(useValue / 10000000).toFixed(2)}Cr`;
    if (abs >= 100000)   return `${(useValue / 100000).toFixed(2)}L`;
    if (abs >= 1000)     return `${(useValue / 1000).toFixed(2)}k`;
    return parseFloat(useValue.toFixed(2));
  };

  const calculatePercentage = (value, highestValue) => {
    if (highestValue === 0) return 0;
    return ((value / highestValue) * 100).toFixed(2);
  };


  const handleTimeChange = () => {
    const time = selectedTimePicked;

    // Convert to 24-hour format
    let hour = time.hour;
    if (time.period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (time.period === 'AM' && hour === 12) {
      hour = 0;
    }

    // Format hour and minute to 2-digit format
    const formattedTime = `${String(hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
    setSelectedTime(formattedTime);
    timeRef.current = formattedTime;
    // Confirm loads the chain for that time right away (once a date has been chosen)
    if (selectedDateForStock) fetchData(formattedTime);
    // console.log("formattedTime",formattedTime); 
    //   console.log("selectedTime",selectedTime); // Output: "09:15"
  };

  const handleButtonClick = () => {
    setShowTimePicker(true);
  };

  const maxOpenInterest = Math.max(...data.map(item => item.CE?.OpenInterest ?? 0));
  const maxVolume = Math.max(...data.map(item => item.CE?.TotalQtyTraded ?? 0));
  const maxOIChng = Math.max(...data.map(item => item.CE?.OpenInterestChange ?? 0));

  const tradeTimes = ['9:30AM', '10:40AM', '11:30AM', '12:20PM', '1:10PM', '2:00PM', '2:50PM', '3:40PM'];

  const data123 = data.map((item, index) => ({
    LastTradeTime: tradeTimes[index] || '', // Assigns trade times statically based on index
    OpenInterest: calculatePercentage(item.CE?.OpenInterest ?? 0, maxOpenInterest),
    volume: calculatePercentage(item.CE?.TotalQtyTraded ?? 0, maxVolume),
    OIChng: calculatePercentage(item.CE?.OpenInterestChange ?? 0, maxOIChng),
  }));
  // console.log('date:', selectedDate);

  // Expiry calendar: the next upcoming Tuesday plus every Tuesday that has already occurred
  // (today included when it is a Tuesday), going back EXPIRY_LOOKBACK_DAYS, recomputed from the
  // current IST date on load. Later Tuesdays and all other days stay disabled.
  useEffect(() => {
    const EXPIRY_LOOKBACK_DAYS = 365;
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date(Date.now() + 330 * 60 * 1000); // IST calendar date (read with UTC getters)
    const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    day.setUTCDate(day.getUTCDate() + (((2 - day.getUTCDay() + 7) % 7) || 7)); // next Tuesday after today
    const tuesdays = [];
    for (let back = 0; back <= EXPIRY_LOOKBACK_DAYS; back += 7) {
      const d = new Date(day.getTime() - back * 86400000);
      tuesdays.push(`${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`);
    }
    setExpiryDates(tuesdays);
  }, []);


  const handleExpiryDateChange = async (event) => {
    setSelectedExpiryDate(event.target.value)

    const expiryDate = event.target.value;

    // Trade dates for the expiry: its own week — the expiry day and the 6 days before it
    // (e.g. Wed–Tue for a Tuesday expiry) — without Saturdays and Sundays.
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const m = String(expiryDate || '').match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
    const monthIndex = m ? MONTHS.findIndex((mon) => mon.toLowerCase() === m[2].toLowerCase()) : -1;
    const nextDates = [];
    if (monthIndex !== -1) {
      const expiry = Date.UTC(Number(m[3]), monthIndex, Number(m[1]));
      for (let back = 0; back < 7; back++) {
        const d = new Date(expiry - back * 86400000);
        if (d.getUTCDay() === 0 || d.getUTCDay() === 6) continue;
        nextDates.push(`${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`);
      }
    }
    setDates(nextDates);
    // Keep the chosen trade date only if it belongs to the new expiry.
    setSelectedDateForStock((current) => (nextDates.includes(current) ? current : ''));

    setShowClock(false);  // Reset the clock visibility when expiry date changes
  };

  const handleDateChange = (event) => {
    // console.log('expiry Date' , event.target.value);
    setSelectedDateForStock(event.target.value);

    setShowClock(true);  // Show the clock when a date is selected
  };

  const ivCalculate = (value) => {
    // console.log("iv",value)
    const iv = (spotPrice - value?.strikePrice)
    const tv = value?.CE?.LastTradePrice - iv
    return tv.toFixed(2);
  }

  const ivCalculatePE = (value) => {
    const iv = (value?.strikePrice - spotPrice)
    const tv = value?.CE?.LastTradePrice - iv
    return tv.toFixed(2);
  }

  const countCprVolume = (value) => {
    let totalCeOI = 0;
    let totalPeOI = 0;

    let obj = {};
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );
    let middleValues = value

    // console.log("middleValues", middleValues);

    // Iterate over the middleValues to sum the quantities
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.TotalQtyTraded || 0;  // Add to totalCeOI, use 0 if undefined
      totalPeOI += item?.PE?.TotalQtyTraded || 0;  // Add to totalPeOI, use 0 if undefined
    });

    // Calculate PCR OI (Put/Call Ratio)
    const pcrOi = totalCeOI === 0 ? 0 : totalCeOI / totalPeOI;

    obj.totalCeOI = totalCeOI.toFixed(2);  // Fixed to 2 decimal places
    obj.totalPeOI = totalPeOI.toFixed(2);  // Fixed to 2 decimal places
    obj.pcrOi = pcrOi.toFixed(2);  // Fixed to 2 decimal places

    return obj;
  };


  const countPcrOIchg = (value) => {
    let totalCeOI = 0; // Initialize to 0 for accumulation
    let totalPeOI = 0; // Initialize to 0 for accumulation

    let obj = {};

    // Extract a portion of the array to work with
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );

    let middleValues = value

    // Accumulate OpenInterestChange values for CE and PE
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.OpenInterestChange || 0;  // Accumulate CE OpenInterestChange
      totalPeOI += item?.PE?.OpenInterestChange || 0;  // Accumulate PE OpenInterestChange
    });

    // Calculate the Put/Call Ratio (PCR) OI change (avoid division by 0)
    const pcrOi = totalCeOI === 0 ? 0 : totalPeOI / totalCeOI;

    // Format the result with two decimal places
    obj.totalCeOI = totalCeOI.toFixed(2);
    obj.totalPeOI = totalPeOI.toFixed(2);
    obj.pcrOi = pcrOi.toFixed(2);

    return obj;
  };


  const countPcrOI = (value) => {
    let totalCeOI = 0; // Initialize for accumulation
    let totalPeOI = 0; // Initialize for accumulation

    let obj = {};
    // let middleValues = value.slice(
    //   Math.max(0, Math.floor(value.length / 2) - 7),
    //   Math.min(value.length, Math.floor(value.length / 2) + 8)
    // );
    let middleValues = value

    // Accumulate OpenInterest for both CE and PE
    middleValues?.forEach((item) => {
      totalCeOI += item?.CE?.OpenInterest || 0;  // Add OpenInterest from CE
      totalPeOI += item?.PE?.OpenInterest || 0;  // Add OpenInterest from PE
    });

    // Calculate PCR OI (Put/Call Ratio)
    const pcrOi = totalCeOI === 0 ? 0 : totalPeOI / totalCeOI;

    // Format the result to two decimal places
    obj.totalCeOI = totalCeOI.toFixed(2);
    obj.totalPeOI = totalPeOI.toFixed(2);
    obj.pcrOi = pcrOi.toFixed(2);

    return obj;
  };


  const countUperHeader = (value) => {
    let upper = [];
    let lower = [];

    let upperSum = 0;
    let lowerSum = 0;
    let upperSumPE = 0;
    let lowerSumPE = 0;
    let ltpChg = 0;
    let ltpChgPE = 0;
    let oiChg = 0;
    let oiChgPE = 0;
    let itmTv = 0;
    let itmTvPE = 0;

    value?.forEach((item) => {
      ltpChg += item?.CE?.PriceChange;
      ltpChgPE += item?.PE?.PriceChange
      oiChg += item?.CE?.OpenInterestChange;
      oiChgPE += item?.PE?.OpenInterestChange
      if (item.strikePrice < spotPrice) {
        upperSum += item.CE.LastTradePrice;
        upperSumPE += item.PE.LastTradePrice;
        upper.push(item);
      } else {
        lowerSum += item.CE.LastTradePrice;
        lowerSumPE += item.PE.LastTradePrice;
        lower.push(item);
      }
    });

    // console.log("upperSum", upperSum, "lowerSum", lowerSum);

    if (upperSum > lowerSum) {
      // ITM
      upper.forEach((item) => {
        const iv = (spotPrice - item?.strikePrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTv += tv;
      });
    } else {
      // OTM
      lower.forEach((item) => {
        const iv = (spotPrice - item?.strikePrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTv += tv;

      });
    }

    if (upperSumPE > lowerSumPE) {
      // ITM
      upper.forEach((item) => {
        const iv = (item?.strikePrice - spotPrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTvPE += tv;
      });
    } else {
      // OTM
      lower.forEach((item) => {
        const iv = (item?.strikePrice - spotPrice);
        const tv = item?.CE?.LastTradePrice - iv;
        itmTvPE += tv;
      });
    }

    const oichagPePer = (oiChgPE * 100 / (oiChgPE + oiChg))
    const oichagPer = (oiChg * 100 / (oiChg + oiChgPE))

    const obj = {
      itmTv: itmTv.toFixed(2),
      itmTvPE: itmTvPE.toFixed(2),
      ltpChg: ltpChg.toFixed(2),
      ltpChgPE: ltpChgPE.toFixed(2),
      oichagPer: oichagPer.toFixed(2),
      oichagPePer: oichagPePer.toFixed(2)
    };

    return obj;
  };
  const onStrikePriceClick = (item, index) => {
    // console.log(index)
    setSelectedStrikePrice(item.strikePrice);

    const d = item?.PE?.LastTradePrice - data[index + 1]?.CE?.LastTradePrice;

    const m = item?.CE?.LastTradePrice - data[index - 1]?.PE?.LastTradePrice;
    const gamma = item?.CE?.Gamma || 0;
    const mP = (gamma * m) / 100;
    const dP = (gamma * d) / 100;

    // console.log("mp",mP ,"................m",m,"................d",d,"................dP",dP ,".............d + dP",d + dP ,"...............m + m p" ,m + mP, "................spotPrice",spotPrice)
    setResistance((d + dP) + (spotPrice || 0));
    setSupport((spotPrice || 0) - (m + mP));


    console.log(firstOptionChain?.data)

    // 9:30 AM values: the strike and its shown neighbours in the selected day's 9:30 chain,
    // or AOC's 09:15–09:29 averages when the API has them for the same pair of strikes.
    const firstData = firstOptionChain?.data || [];
    const firstAt = (strike) => firstData.find((optionItem) => Number(optionItem.strikePrice) === Number(strike));
    const item2 = firstAt(item.strikePrice);
    const nextStrike = data[index + 1]?.strikePrice;
    const prevStrike = data[index - 1]?.strikePrice;
    const d2 = item2?.PE?.LastTradePrice - firstAt(nextStrike)?.CE?.LastTradePrice;
    const m2 = item2?.CE?.LastTradePrice - firstAt(prevStrike)?.PE?.LastTradePrice;
    const averages = firstOptionChain?.averageReversal || {};
    const averageNext = firstOptionChain?.averageReversalNext || {};
    const averageFor = (strike, next) => (strike != null && next != null
      && averages[Number(strike)] != null && Number(averageNext[Number(strike)]) === Number(next)
      ? Number(averages[Number(strike)])
      : null);
    setResistance2(averageFor(item.strikePrice, nextStrike) ?? d2 + firstOptionChain?.spot?.LastTradePrice);
    setSupport2(averageFor(prevStrike, item.strikePrice) ?? firstOptionChain?.spot?.LastTradePrice - m2);


  }
  console.log("support2,resistance2", support2, resistance2)

  // 9:30 AM reversal values of the selected day (09:15–09:29 averages from the API), the red
  // strike and the Spot Chart lines — the same rules as the Option Chain page.
  const reversal930 = useMemo(
    () => buildReversal930(data, firstOptionChain, spotPrice),
    [data, firstOptionChain, spotPrice]
  );
  const resistanceHighlightStrike = useMemo(() => redStrikeFor(reversal930, spotPrice), [reversal930, spotPrice]);
  const spotChartLevels = useMemo(() => chartLevelsFor(reversal930, resistanceHighlightStrike), [reversal930, resistanceHighlightStrike]);

  const countResistans = (item, index) => {
    const d = item?.PE?.LastTradePrice - data[index + 1]?.CE?.LastTradePrice;

    const m = item?.CE?.LastTradePrice - data[index - 1]?.PE?.LastTradePrice;

    const resistance = d + spotPrice;
    const support = spotPrice - m;

    return {
      resistance,
      support
    }
  }

  const countResistansAndSupport = (item, index) => {
    if (item?.CE?.OpenInterestChange >= item?.PE?.OpenInterestChange && item?.CE?.OpenInterestChange >= 2 * item?.PE?.OpenInterestChange && item?.CE?.OpenInterestChange > 0) {
      return 'Strong R'
    } else if (item?.CE?.OpenInterestChange >= item?.PE?.OpenInterestChange && item?.CE?.OpenInterestChange < 2 * item?.PE?.OpenInterestChange) {
      return 'R'
    } else if (item?.PE?.OpenInterestChange >= item?.CE?.OpenInterestChange && item?.PE?.OpenInterestChange >= 2 * item?.CE?.OpenInterestChange && item?.CE?.OpenInterestChange > 0) {
      return 'Strong S'
    } else if (item?.PE?.OpenInterestChange >= item?.CE?.OpenInterestChange && item?.PE?.OpenInterestChange < 2 * item?.CE?.OpenInterestChange) {
      return 'S'
    }
    else {
      return ''
    }
  }

  const countSupportAndResistance = (item, index) => {
    if (item?.PE?.OpenInterestChange >= item?.CE?.OpenInterestChange && item?.PE?.OpenInterestChange >= 2 * item?.CE?.OpenInterestChange && item?.CE?.OpenInterestChange > 0) {
      return 'Strong S'
    } else if (item?.PE?.OpenInterestChange >= item?.CE?.OpenInterestChange && item?.PE?.OpenInterestChange < 2 * item?.CE?.OpenInterestChange) {
      return 'S'
    } else {
      return ''
    }
  }

  // const onStrikePriceClick = (item,index) => {
  //   console.log(index)
  //   setSelectedStrikePrice(item.strikePrice);

  //   const d = item?.PE?.LastTradePrice - data[index+1]?.CE?.LastTradePrice;

  //   const m = item?.CE?.LastTradePrice - data[index-1]?.PE?.LastTradePrice;

  //   const mP = (item?.CE?.Gamma * m )/100;
  //   const dP = (item?.CE?.Gamma * d )/100;

  //   console.log("mp",mP ,"................m",m,"................d",d,"................dP",dP ,".............d + dP",d + dP ,"...............m + m p" ,m + mP, "................spotPrice",spotPrice)
  //   setResistance((d +dP) + spotPrice) ;

  //   setSupport(spotPrice - (m + mP));


  //   console.log(firstOptionChain?.data)

  //   const item2 = firstOptionChain?.data?.find((optionItem) => optionItem.strikePrice === item.strikePrice);

  //   console.log("item2",item2)

  //   const d2 = item2?.PE?.LastTradePrice - data[index+1]?.CE?.LastTradePrice;

  //   const m2 = item2?.CE?.LastTradePrice - data[index-1]?.PE?.LastTradePrice;

  //   setResistance2(d2 + firstOptionChain?.spot?.LastTradePrice) ;

  //   setSupport2(firstOptionChain?.spot?.LastTradePrice - m2);


  // }

  // const countResistans = (item ,index) => {
  //   const d = item?.PE?.LastTradePrice - data[index+1]?.CE?.LastTradePrice;

  //   const m = item?.CE?.LastTradePrice - data[index-1]?.PE?.LastTradePrice;

  //   const resistance = d + spotPrice;
  //   const support = spotPrice - m;

  //   return  {
  //     resistance,
  //     support
  //   }
  // }


  const formatNumber2 = (value) => {
    let useValue;

    if (value >= 100000) {
      return `${(value / 100000).toFixed(2)}L`; // Format as lakhs
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}k`; // Format as thousands
    } else {
      return value; // Return as is
    }


  };

  let isSpotPrice;

  if (!isLoggedIn) {
    // Redirect to login if not logged in
    return <Navigate to="/" />;
  }
  // new code // Step 1: Unique, sorted strike prices
  const strikePrices = [...new Set(data.map(item => parseFloat(item.strikePrice)))].sort((a, b) => a - b);

  // Step 2: Identify lower & upper for nearest strikes
  let lower = null, upper = null;
  for (let i = 0; i < strikePrices.length - 1; i++) {
    if (strikePrices[i] <= spotPrice && strikePrices[i + 1] >= spotPrice) {
      lower = strikePrices[i];
      upper = strikePrices[i + 1];
      break;
    }
  }

  // Step 3: Define 2 lower and 2 upper
  const lowerStrikes = strikePrices.filter(price => price < spotPrice);
  const upperStrikes = strikePrices.filter(price => price > spotPrice);
  const twoLower = lowerStrikes.slice(-2);
  const twoUpper = upperStrikes.slice(0, 2);

  // Step 4: Initialize all totals
  let totalCE_1 = 0, totalPE_1 = 0;
  let totalCE_2 = 0, totalPE_2 = 0;
  let totalCELower = 0, totalPELower = 0;
  let totalCEUpper = 0, totalPEUpper = 0;

  // Step 5: Loop once and compute all
  strikePrices.forEach(strike => {
    const ce = data.find(item => item.CE && parseFloat(item.strikePrice) === strike)?.CE?.OpenInterestChange || 0;
    const pe = data.find(item => item.PE && parseFloat(item.strikePrice) === strike)?.PE?.OpenInterestChange || 0;

    if (strike === lower || strike === upper) {
      totalCE_1 += ce;
      totalPE_1 += pe;
    }

    if (twoLower.includes(strike) || twoUpper.includes(strike)) {
      totalCE_2 += ce;
      totalPE_2 += pe;
    }

    if (strike < spotPrice) {
      totalCELower += ce;
      totalPELower += pe;
    }

    if (strike > spotPrice) {
      totalCEUpper += ce;
      totalPEUpper += pe;
    }
  });

  const totalCE_All = totalCELower + totalCEUpper;
  const totalPE_All = totalPELower + totalPEUpper;
  const topOI = [];

  data.forEach((item) => {
    if (item.CE) {
      topOI.push({
        strikePrice: item.strikePrice,
        type: "CE",
        change: item.CE.OpenInterestChange,
      });
    }
    if (item.PE) {
      topOI.push({
        strikePrice: item.strikePrice,
        type: "PE",
        change: item.PE.OpenInterestChange,
      });
    }
  });

  // Sort by highest Open Interest Change
  const top5OI = topOI.sort((a, b) => b.change - a.change).slice(0, 5);
  // mew code 2
  // Step 1: Extract and sort unique strike prices
  const uniqueStrikePrices = [...new Set(data.map(entry => parseFloat(entry.strikePrice)))].sort((a, b) => a - b);

  // Step 2: Find 3 closest strike prices to the current spot price
  const nearbyStrikes = uniqueStrikePrices
    .map(price => ({ value: price, difference: Math.abs(price - spotPrice) }))
    .sort((a, b) => a.difference - b.difference)
    .slice(0, 3)
    .map(entry => entry.value)
    .sort((a, b) => a - b); // Optional sort for presentation

  // Step 3: Calculate Open Interest Change and Total
  let cumulativeCallOIChange = 0;
  let cumulativePutOIChange = 0;

  const analyzedStrikes = nearbyStrikes.map(strikeValue => {
    const matchedOption = data.find(opt => parseFloat(opt.strikePrice) === strikeValue);
    const callChange = matchedOption?.CE?.OpenInterestChange || 0;
    const putChange = matchedOption?.PE?.OpenInterestChange || 0;

    cumulativeCallOIChange += callChange;
    cumulativePutOIChange += putChange;

    let changeComparison = '';
    if (callChange > putChange) changeComparison = 'CALL > PUT';
    else if (putChange > callChange) changeComparison = 'PUT > CALL';
    else changeComparison = 'CALL = PUT';

    return {
      strikePrice: strikeValue,
      callOIChange: callChange,
      putOIChange: putChange,
      changeComparison
    };
  });

  // Step 4: Final OI Change Comparison Summary
  let finalOIComparison = '';
  if (cumulativeCallOIChange > cumulativePutOIChange) finalOIComparison = 'TOTAL: CW > PW';
  else if (cumulativePutOIChange > cumulativeCallOIChange) finalOIComparison = 'TOTAL: PW > CW';
  else finalOIComparison = 'TOTAL: CW = PW';



  const calculateOpenInterestChange = () => {
    if (!data || data.length === 0) return;

    // Filter kar rahe strike prices ke range ke andar
    const filteredData = data.filter(item => {
      const strike = parseFloat(item.strikePrice);
      return strike >= parseFloat(rangeStart) && strike <= parseFloat(rangeEnd);
    });

    // CE and PE OpenInterestChange ka total
    let totalCallOIChange = 0;
    let totalPutOIChange = 0;
    let totalCallOI = 0;
    let totalPutOI = 0;

    filteredData.forEach(item => {
      if (item.CE) {
        totalCallOIChange += item.CE.OpenInterestChange || 0;
        totalCallOI += item.CE.OpenInterest || 0;
      }
      if (item.PE) {
        totalPutOIChange += item.PE.OpenInterestChange || 0;
        totalPutOI += item.PE.OpenInterest || 0;
      }
    });

    setCallOIChange(totalCallOIChange / 100000);
    setPutOIChange(totalPutOIChange / 100000);
    setCallOI(totalCallOI / 100000);
    setPutOI(totalPutOI / 100000);




    setDataCallPutOIChange([
      { name: "CALL", value: totalCallOIChange / 1000 },
      { name: "PUT", value: totalPutOIChange / 1000 }
    ]);
    setDataCallPutOI([
      { name: "CALL", value: totalCallOI / 1000 },
      { name: "PUT", value: totalPutOI / 1000 }
    ]);
  };
  // console.log(">>>>",expiryDates);
  return (
    <div>
      <section>
        <div className="back-tester-content-section ">
          {/* <div>
            {(localStorage.getItem("stock") === "NIFTY" || localStorage.getItem("stock") === "SENSEX") && (
              <strong style={{ color: 'blue', fontWeight: 'bold', display: 'block', fontSize: '20px', textAlign: 'center' }}>
                WEEKLY RANGE: {weeklyUpper.toFixed(2)} ↕️ {weeklyDown.toFixed(2)}
              </strong>
            )}
          </div><br /> */}
          <div className="container-fluid">
            {/* Compact toolbar (AOC style): one row of 38px controls, no labels above. */}
            <div className="select-expire-date-time-field bt-compact-controls">
              <ul className="list-unstyled d-flex justify-content-start align-items-center flex-wrap">
                <li>
                  <CalendarPicker
                    size="sm"
                    value={selectedExpiryDate}
                    dates={expiryDates}
                    onChange={(date) => handleExpiryDateChange({ target: { value: date } })}
                    placeholder="Select Expiry Date"
                    dialogLabel="Choose expiry date"
                    emptyText="No expiry data available"
                  />
                </li>
                <li>
                  <CalendarPicker
                    size="sm"
                    value={selectedDateForStock}
                    dates={dates}
                    onChange={(date) => handleDateChange({ target: { value: date } })}
                    disabled={!selectedExpiryDate} // Disable until an expiry date is selected
                    placeholder="Select Date"
                    dialogLabel="Choose trade date"
                    emptyText="No trading dates for this expiry"
                  />
                </li>
                {/* <li>
                            <p className="desc">Time</p>
                            <button
                              className="site-btn btn-blue-o"
                              onClick={handleButtonClick}
                              disabled={!selectedDateForStock}
                            >
                              {selectedTime ? selectedTime : 'Select Time'}
                            </button>
              
                            <TimePicker
                              onChange={handleTimeChange}
                              value={selectedTime}
                              disableClock={true} // Optional: Disables the analog clock
                            />
                          </li>     */}
                <li>
                  <button className="site-btn btn-blue-o" data-bs-toggle="modal" data-bs-target="#timePickerModel" disabled={!selectedDateForStock}>
                    <i className="fa fa-clock-o" /> <small>TIME</small> {selectedTime ? selectedTime : 'Select Time'}
                  </button>

                  {/* {showTimePicker && (
          <div className="time-picker">
            <div className="time-left">
              <div className="time-show">
                <div className="time-selector" onClick={handleHourClick}>
                  <span>{selectedTime.hour}</span>
                  {isHourPickerVisible && (
                    <div className="hour-picker">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <button key={i} onClick={() => handleNumberClick(i === 0 ? 12 : i)}>
                          {i === 0 ? 12 : i}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span>:</span>
                <div className="time-selector" onClick={handleMinuteClick}>
                  <span>{selectedTime.minute.toString().padStart(2, "0")}</span>
                  {isMinutePickerVisible && (
                    <div className="minute-picker">
                      {Array.from({ length: 60 }).map((_, i) => (
                        <button key={i} onClick={() => handleMinuteSelection(i)}>
                          {i.toString().padStart(2, "0")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span> {selectedTime.period}</span>
              </div>
              <div className="am-pm-toggle">
                <button onClick={() => togglePeriod("AM")} className={selectedTime.period === "AM" ? "selected" : ""}>
                  AM
                </button>
                <button onClick={() => togglePeriod("PM")} className={selectedTime.period === "PM" ? "selected" : ""}>
                  PM
                </button>
              </div>
            </div>
            <div className="time-right">
              <div className="clock-face">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="#ddd" stroke="black" strokeWidth="2" />
                  {Array.from({ length: 12 }).map((_, i) => {
                    const angle = ((i * 30) - 90) * Math.PI / 180;
                    return (
                      <text
                        style={{ cursor: "pointer" }}
                        key={i}
                        x={50 + 35 * Math.cos(angle)}
                        y={50 + 35 * Math.sin(angle) + 4}
                        textAnchor="middle"
                        fontSize="8"
                        fill="black"
                        onClick={() => handleNumberClick(i === 0 ? 12 : i)}
                      >
                        {i === 0 ? 12 : i}
                      </text>
                    );
                  })}
                  <line
                    x1="50"
                    y1="50"
                    x2={50 + 30 * Math.cos((clockHandAngles.hour - 90) * Math.PI / 180)}
                    y2={50 + 30 * Math.sin((clockHandAngles.hour - 90) * Math.PI / 180)}
                    stroke="black"
                    strokeWidth="3"
                  />
                  <line
                    x1="50"
                    y1="50"
                    x2={50 + 40 * Math.cos((clockHandAngles.minute - 90) * Math.PI / 180)}
                    y2={50 + 40 * Math.sin((clockHandAngles.minute - 90) * Math.PI / 180)}
                    stroke="black"
                    strokeWidth="2"
                  />
                  <circle cx="50" cy="50" r="3" fill="black" />
                </svg>
              </div>
              <div className="buttons">
                <button onClick={() => setShowTimePicker(false)}>Cancel</button>
                <button onClick={() => alert(`Selected Time: ${selectedTime.hour}:${selectedTime.minute.toString().padStart(2, "0")} ${selectedTime.period}`)}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )} */}
                </li>
                <li>
                  <button
                    className="site-btn btn-blue fetch-btn"
                    onClick={fetchData}
                    disabled={loading} // Disable the button if data is being fetched
                  >
                    <i className="fa fa-search" /> {loading ? 'Loading...' : 'Fetch Data'}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          {/* Call Vs Put OI / Greeks / … / Select Strike buttons, below the expiry / date / time row */}
          <div>
            <button x
              class="btn btn-primary mb-2"
              data-bs-toggle="modal"
              data-bs-target="#callVsPutOI"
              style={{
                backgroundColor: '#f77c22',
                border: '2px solid #f77c22',
                borderRadius: '10px',
                padding: '5px 15px',
                fontWeight: 'bold',
                fontSize: '14px',
                marginBottom: "10px"
              }}
            >
              Call Vs Put OI
            </button>&nbsp;
            <div style={styles.dropdown}>
              <button style={styles.dropbtn} onClick={toggleDropdown}>
                GREEKS ⮟
              </button>
              {open && (
                <div style={styles.dropdownContent}>
                  {greekitems.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.dropdownItem,
                        ...(index === greekitems.length - 1 ? styles.lastItem : {}),
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e46c15'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#f77c22'}
                      onClick={() => handleGreekSelect(item)} // use this
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
              {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}> */}
              &nbsp; <button
                onClick={() => toggleButton('IV')}
                style={{
                  backgroundColor: activeButtons.includes('IV') ? '#f77c22' : '#fff9e7',
                  border: '2px solid #f77c22',
                  color: activeButtons.includes('IV') ? 'white' : '#f77c22',
                  borderRadius: '10px',
                  padding: '5px 15px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                IV
              </button>

              &nbsp;<button
                onClick={() => toggleButton('IV/&/Y')}
                style={{
                  backgroundColor: activeButtons.includes('IV/&/Y') ? '#f77c22' : '#fff9e7',
                  border: '2px solid #f77c22',
                  color: activeButtons.includes('IV/&/Y') ? 'white' : '#f77c22',
                  borderRadius: '10px',
                  padding: '5px 10px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                I.V./&/Y
              </button>&nbsp;
              <button
                class="btn btn-primary mb-2"
                data-bs-toggle="modal"
                data-bs-target="#top5openinterestchnage"
                style={{
                  backgroundColor: '#f77c22',
                  border: '2px solid #f77c22',
                  borderRadius: '10px',
                  padding: '5px 15px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: "10px"
                }}
              >
                Top 5 OpenInterestChange
              </button>
              &nbsp;

              {/* {(localStorage.getItem("stock") === "NIFTY" || localStorage.getItem("stock") === "BANKNIFTY") && ( */}
              <button
                className="btn btn-primary mb-2"
                data-bs-toggle="modal"
                data-bs-target="#callAndPutWriting"
                style={{
                  backgroundColor: '#f77c22',
                  border: '2px solid #f77c22',
                  borderRadius: '10px',
                  padding: '5px 15px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                }}
              >
                Call Writing And Put Writing
              </button>
              <div style={{ ...styles.dropdown, display: 'inline-block', position: 'relative', marginLeft: '10px' }}>
                <button style={styles.dropbtn} onClick={() => setStrikeDropdownOpen((v) => !v)}>
                  {selectedStrike || 'Select Strike'} ⮟
                </button>
                {strikeDropdownOpen && (
                  <div style={styles.dropdownContent}>
                    {['', ...STRIKE_OPTIONS].map((option, index, list) => (
                      <div
                        key={option || 'all'}
                        style={{
                          ...styles.dropdownItem,
                          ...(index === list.length - 1 ? styles.lastItem : {}),
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#e46c15'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f77c22'}
                        onClick={() => { setSelectedStrike(option); setStrikeDropdownOpen(false); }}
                      >
                        {option || 'All strikes (default)'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Spot Chart popup (moved here from the top header) */}
              <button
                type="button"
                className="btn btn-primary mb-2"
                title="Spot chart with support / resistance"
                data-bs-toggle="modal"
                data-bs-target={`#${SPOT_CHART_MODAL_ID}`}
                style={{
                  backgroundColor: '#f77c22',
                  border: '2px solid #f77c22',
                  borderRadius: '10px',
                  padding: '5px 15px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginLeft: '10px',
                }}
              >
                <i className="fa fa-line-chart me-2" />
                Chart
              </button>


              {/* </div> */}
            </div>

          </div>
        </div>
      </section>

      <section className="lot-size-section bt-table-section">
        <div className="container-fluid px-0">
          <div className="lot-size-inner otw-sticky-box" ref={tableBoxRef}>
            <table className="table-left">
              <tbody>
                {/* Top header row: one cell over the call columns, the date / time over the strike
                    column, one cell over the put columns — spans follow the columns shown. */}
                <tr ref={headerRow1Ref} className="table-first bt-header-top otw-sticky-row1" style={{ background: "red", color: "#fff" }}>
                  <th colSpan={sideColumns}>
                    <div className="bt-header-stats">
                      <span>TvItm:{countUperHeader(data).itmTv}</span>
                      <span>LTPChg:{countUperHeader(data).ltpChg}</span>
                      <span>OIChg:{countUperHeader(data).oichagPer}%</span>
                      <span>(CE)R : 21300</span>
                    </div>
                  </th>
                  <th className="strike-price" style={{ background: "black", color: "#fff" }}>{appliedQuery ? <>{appliedQuery.date}<br />{appliedQuery.time}</> : '—'}</th>
                  <th colSpan={sideColumns} className="table-right" style={{ background: "green", color: "#fff" }}>
                    <div className="bt-header-stats">
                      <span>(PE)R : 21300</span>
                      <span>OIChg:{countUperHeader(data).oichagPePer}%</span>
                      <span>LTPChg:{countUperHeader(data).ltpChgPE}</span>
                      <span>TvItm:{countUperHeader(data).itmTvPE}</span>
                    </div>
                  </th>
                </tr>
                <tr style={{ background: "red", color: "#fff" }}>
                  {selectedGreeks.includes('GAMMA') && <th>Gamma</th>}
                  {selectedGreeks.includes('VEGA') && <th>Vega</th>}
                  {selectedGreeks.includes('THETA') && <th>Theta</th>}
                  {selectedGreeks.includes('DELTA') && <th>Delta</th>}
                  {activeButtons.includes('IV') && <th>IV</th>}
                  {activeButtons.includes('IV/&/Y') && <th>IV/&amp;/y</th>}
                  <th>OI Chg</th>
                  <th>OI</th>
                  <th>Volume</th>
                  <th>LTP/Chg(TV)</th>
                  {showSwing && (
                    <th style={{ color: '#fff', backgroundColor: '#ff505c' }}>Probable <br />Swing High</th>
                  )}
                  <th className="strike-price text-dark">SP:PCR Oi/OiC</th>
                  {showSwing && (
                    <th style={{ color: '#fff', backgroundColor: '#2bba2b' }} className="table-right">Probable <br />Swing Low</th>
                  )}
                  <th className="table-right" style={{ background: "green", color: "#fff" }}>LTP/Chg(TV)</th>
                  <th className="table-right" style={{ background: "green", color: "#fff" }}>Volume</th>
                  <th className="table-right" style={{ background: "green", color: "#fff" }}>OI</th>
                  <th className="table-right" style={{ background: "green", color: "#fff" }}>OI Chg</th>
                  {activeButtons.includes('IV/&/Y') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>IV/&amp;/y</th>}
                  {activeButtons.includes('IV') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>IV</th>}
                  {selectedGreeks.includes('GAMMA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Gamma</th>}
                  {selectedGreeks.includes('VEGA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Vega</th>}
                  {selectedGreeks.includes('THETA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Theta</th>}
                  {selectedGreeks.includes('DELTA') && <th className="table-right" style={{ backgroundColor: 'green', color: 'white' }}>Delta</th>}
                </tr>
                {data && Array.isArray(data) ? (
                  data.map((item, index) => {
                    localStorage.getItem('reverseTable') === 'true' ? isSpotPrice =
                      spotPrice &&
                      item.strikePrice <= spotPrice &&
                      (!data[index - 1] ||
                        spotPrice < data[index - 1].strikePrice) : isSpotPrice =
                    spotPrice &&
                    item.strikePrice >= spotPrice &&
                    (!data[index - 1] ||
                      spotPrice > data[index - 1].strikePrice);
                    return (
                      <React.Fragment key={index}>
                        {isSpotPrice && getSpotPriceRow()}
                        <tr style={{ backgroundColor: isSpotPrice ? '#d1e7dd' : 'transparent' }}>
                          {selectedGreeks.includes('GAMMA') && (
                            <td>
                              {parseFloat(item.CE?.Gamma ?? 0).toFixed(4)}
                            </td>
                          )}
                          {selectedGreeks.includes('VEGA') && (
                            <td>
                              {parseFloat(item.CE?.Vega ?? 0).toFixed(2)}
                            </td>
                          )}

                          {selectedGreeks.includes('THETA') && (
                            <td>
                              {parseFloat(item.CE?.Theta ?? 0).toFixed(2)}
                            </td>
                          )}

                          {selectedGreeks.includes('DELTA') && (
                            <td>
                              {parseFloat(item.CE?.Delta ?? 0).toFixed(2)}
                            </td>
                          )}
                          {activeButtons.includes('IV') && <td>{formatIV(item.CE?.IV)}</td>}
                          {activeButtons.includes('IV/&/Y') &&
                            <td>
                              <button>
                                {formatIV(item.CE?.IV)}
                                <br />
                                {parseFloat(item.CE?.Gamma ?? 0).toFixed(2)}/
                                {parseFloat(item.CE?.Theta ?? 0).toFixed(2)}
                              </button>
                            </td>
                          }
                          <td>
                            <button
                              style={{
                                backgroundColor:
                                  item.CE?.OpenInterestChange === highestCEOIChange
                                    ? "#b7071c" // Red for the highest
                                    : item.CE?.OpenInterestChange === secondHighestCEOIChange && calculatePercentage(item.CE?.OpenInterestChange ?? 0, highestCEOIChange) >= 75
                                      ? "#E2CF64" // Yellow for second highest if percentage >= 75
                                      : item.CE?.OpenInterestChange === thirdHighestCEOIChange && calculatePercentage(item.CE?.OpenInterestChange ?? 0, highestCEOIChange) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2" // Light Yellow for third highest if percentage >= 75
                                        : "inherit", // Default background if no match,
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#oichartModel"
                              onClick={() => setOiChartsInstrument(item?.CE?.InstrumentIdentifier)}
                            >
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                  item.CE?.OpenInterestChange ?? 0,
                                  highestCEOIChange
                                ) : formatNumber(
                                  item.CE?.OpenInterestChange ?? 0
                                )}
                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}

                              </div>
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(
                                  item.CE?.OpenInterestChange ?? 0
                                ) : calculatePercentage(
                                  item.CE?.OpenInterestChange ?? 0,
                                  highestCEOIChange
                                )}
                                {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                              </div>
                            </button>
                          </td>
                          <td>
                            <button
                              style={{
                                backgroundColor:
                                  item.CE?.OpenInterest === highestCEOI
                                    ? "#b7071c" // Red for the highest
                                    : item.CE?.OpenInterest === secondHighestCEOI &&
                                      calculatePercentage(item.CE?.OpenInterest ?? 0, highestCEOI) >= 75
                                      ? "#E2CF64" // Yellow for second highest if percentage >= 75
                                      : item.CE?.OpenInterest === thirdHighestCEOI &&
                                        calculatePercentage(item.CE?.OpenInterest ?? 0, highestCEOI) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2" // Light Yellow for third highest if percentage >= 75
                                        : "inherit", // Default background if no match
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#oipercentage1Model"
                              onClick={() => onHistoricsClick(item.CE)}
                            >
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                  item.CE?.OpenInterest ?? 0,
                                  highestCEOI
                                ) : formatNumber(item.CE?.OpenInterest ?? 0)}
                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                              </div>
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.CE?.OpenInterest ?? 0) :
                                  calculatePercentage(
                                    item.CE?.OpenInterest ?? 0,
                                    highestCEOI
                                  )}
                                {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                              </div>
                            </button>
                          </td>
                          <td>
                            <button
                              style={{
                                backgroundColor:
                                  item.CE?.TotalQtyTraded === highestCEVolume
                                    ? "#b7071c" // Red for the highest
                                    : item.CE?.TotalQtyTraded === secondHighestCEVolume &&
                                      calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume) >= 75
                                      ? "#E2CF64" // Yellow for second highest if percentage >= 75
                                      : item.CE?.TotalQtyTraded === thirdHighestCEVolume &&
                                        calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2" // Light Yellow for third highest if percentage >= 75
                                        : "inherit", // Default background if no match
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#volumeChartModel"
                              onClick={() => setSelectedStrikePrice(item.strikePrice)}
                            >
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                  item.CE?.TotalQtyTraded ?? 0,
                                  highestCEVolume
                                ) : formatNumber(item.CE?.TotalQtyTraded ?? 0)}

                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                              </div>
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.CE?.TotalQtyTraded ?? 0) : calculatePercentage(item.CE?.TotalQtyTraded ?? 0, highestCEVolume)}
                                {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                              </div>
                            </button>
                          </td>
                          <td>
                            <button data-bs-toggle="modal" data-bs-target="#LPTcalculatorModel">
                              {item.CE?.LastTradePrice ?? 0}<br />{item?.CE?.PriceChange}/({ivCalculate(item)})
                            </button>
                          </td>
                          {showSwing && (
                            <td style={{ color: '#fff', backgroundColor: '#ff505c' }}>
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#LPTcalculatorModel"
                              >
                                {swingLabel(item, 'CE') && <div style={{ fontWeight: 'bold' }}>{swingLabel(item, 'CE')}</div>}
                                {countResistans(item, index).resistance.toFixed(2)}
                              </button>
                            </td>
                          )}
                          <td
                            className="strike-price"
                            style={
                              resistanceHighlightStrike != null &&
                              Number(item.strikePrice) === Number(resistanceHighlightStrike)
                                ? { backgroundColor: '#b7071c' }
                                : undefined
                            }
                          >
                            <button
                              data-bs-toggle="modal"
                              data-bs-target="#strikePriceModel"
                              onClick={() => onStrikePriceClick(item, index)}
                              style={
                                resistanceHighlightStrike != null &&
                                Number(item.strikePrice) === Number(resistanceHighlightStrike)
                                  ? { backgroundColor: '#b7071c', color: '#fff', width: '100%' }
                                  : undefined
                              }
                            >{item.strikePrice}<br />{pcrUnderStrike(item?.PE?.OpenInterest, item?.CE?.OpenInterest)}/{pcrUnderStrike(item?.PE?.OpenInterestChange, item?.CE?.OpenInterestChange)}</button>
                          </td>

                          {showSwing && (
                            <td className="table-right" style={{ color: '#fff', backgroundColor: '#2bba2b' }}>
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#CombinedpriceModel"
                              >
                                {swingLabel(item, 'PE') && <div style={{ fontWeight: 'bold' }}>{swingLabel(item, 'PE')}</div>}
                                {countResistans(item, index).support.toFixed(2)}
                              </button>
                            </td>
                          )}

                          <td className="table-right">
                            <button data-bs-toggle="modal" data-bs-target="#CombinedpriceModel">
                              {item.PE?.LastTradePrice ?? 0}<br />{item?.PE?.PriceChange}/({ivCalculatePE(item)})
                            </button>
                          </td>
                          <td className="table-right">
                            <button
                              style={{
                                backgroundColor:
                                  item.PE?.TotalQtyTraded === highestPEVolume
                                    ? "#4d9f4e"
                                    : item.PE?.TotalQtyTraded ===
                                      secondHighestPEVolume && calculatePercentage(
                                        item.PE?.TotalQtyTraded ?? 0,
                                        highestPEVolume
                                      ) >= 75
                                      ? "#E2CF64"
                                      : item.PE?.TotalQtyTraded ===
                                        thirdHighestPEVolume && calculatePercentage(
                                          item.PE?.TotalQtyTraded ?? 0,
                                          highestPEVolume
                                        ) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2"
                                        : "inherit",
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#volumeChartModel12"
                              onClick={() => setSelectedStrikePrice(item.strikePrice)}
                            >
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                  item.PE?.TotalQtyTraded ?? 0,
                                  highestPEVolume
                                ) : formatNumber(item.PE?.TotalQtyTraded ?? 0)}
                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                              </div>
                              <div>
                                {localStorage.getItem('perferPercentage') === 'true' ? formatNumber(item.PE?.TotalQtyTraded ?? 0) : calculatePercentage(item.PE?.TotalQtyTraded ?? 0, highestPEVolume)}
                                {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                              </div>
                            </button>
                          </td>
                          <td className="table-right">
                            <button
                              style={{
                                backgroundColor:
                                  item.PE?.OpenInterest === highestPEOI
                                    ? "#4d9f4e"
                                    : item.PE?.OpenInterest ===
                                      secondHighestPEOI && calculatePercentage(
                                        item.PE?.OpenInterest ?? 0,
                                        highestPEOI
                                      ) >= 75
                                      ? "#E2CF64"
                                      : item.PE?.OpenInterest ===
                                        thirdHighestPEOI && calculatePercentage(
                                          item.PE?.OpenInterest ?? 0,
                                          highestPEOI
                                        ) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2"
                                        : "inherit",
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#oipercentage1Model"
                              onClick={() => onHistoricsClick(item.PE)}
                            >
                              <div>
                                {
                                  localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(
                                    item.PE?.OpenInterest ?? 0,
                                    highestPEOI
                                  ) : formatNumber(item.PE?.OpenInterest ?? 0)
                                }
                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                              </div>
                              <div>
                                {localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.PE?.OpenInterest ?? 0) : calculatePercentage(
                                  item.PE?.OpenInterest ?? 0,
                                  highestPEOI
                                )}

                                {localStorage.getItem('preferPercentage') === 'true' ? '' : "%"}
                              </div>
                            </button>
                          </td>
                          <td className="table-right">
                            <button
                              style={{
                                backgroundColor:
                                  item.PE?.OpenInterestChange ===
                                    highestPEOIChange
                                    ? "#4d9f4e"
                                    : item.PE?.OpenInterestChange ===
                                      secondHighestPEOIChange && calculatePercentage(
                                        item.PE?.OpenInterestChange ?? 0,
                                        highestPEOIChange
                                      ) >= 75
                                      ? "#E2CF64"
                                      : item.PE?.OpenInterestChange ===
                                        thirdHighestPEOIChange && calculatePercentage(
                                          item.PE?.OpenInterestChange ?? 0,
                                          highestPEOIChange
                                        ) >= 75 && localStorage.getItem('highThirdHighest') === 'true'
                                        ? "#E5E4C2"
                                        : "inherit",
                              }}
                              data-bs-toggle="modal"
                              data-bs-target="#oichartModel"
                              onClick={() => setOiChartsInstrument(item?.PE?.InstrumentIdentifier)}
                            >
                              <div>
                                {
                                  localStorage.getItem('preferPercentage') === 'true' ? calculatePercentage(item.PE?.OpenInterestChange ?? 0, highestPEOIChange) : formatNumber(item.PE?.OpenInterestChange ?? 0)
                                }

                                {localStorage.getItem('preferPercentage') === 'true' ? '%' : ''}
                              </div>
                              <div>
                                {
                                  localStorage.getItem('preferPercentage') === 'true' ? formatNumber(item.PE?.OpenInterestChange ?? 0) : calculatePercentage(item.PE?.OpenInterestChange ?? 0, highestPEOIChange)
                                }
                                {
                                  localStorage.getItem('preferPercentage') === 'true' ? '' : '%'
                                }
                              </div>
                            </button>
                          </td>
                          {activeButtons.includes('IV/&/Y') &&
                            <td className="table-right" style={{
                              ...(item.strikePrice.toString().endsWith("00") && { backgroundColor: "#ffffae" }),
                            }}>
                              <button
                                data-bs-toggle="modal"
                                data-bs-target="#LPTcalculatorModel"
                              >
                                {formatIV(item.PE?.IV)}
                                <br />
                                {parseFloat(item.PE?.Gamma ?? 0).toFixed(2)}/
                                {parseFloat(item.PE?.Theta ?? 0).toFixed(2)}
                              </button>
                            </td>
                          }
                          {activeButtons.includes('IV') && <td className="table-right" style={
                            item.strikePrice.toString().endsWith("00")
                              ? { backgroundColor: "#ffffae" }
                              : {}
                          }  >{formatIV(item.PE?.IV)}</td>}
                          {selectedGreeks.includes('GAMMA') && (
                            <td className="table-right"
                              style={
                                item.strikePrice.toString().endsWith("00")
                                  ? { backgroundColor: "#ffffae" }
                                  : {}
                              }>
                              {parseFloat(item.PE?.Gamma ?? 0).toFixed(4)}
                            </td>
                          )}
                          {selectedGreeks.includes('VEGA') && (
                            <td className="table-right"
                              style={
                                item.strikePrice.toString().endsWith("00")
                                  ? { backgroundColor: "#ffffae" }
                                  : {}
                              }
                            >
                              {parseFloat(item.PE?.Vega ?? 0).toFixed(2)}
                            </td>
                          )}

                          {selectedGreeks.includes('THETA') && (
                            <td className="table-right"
                              style={
                                item.strikePrice.toString().endsWith("00")
                                  ? { backgroundColor: "#ffffae" }
                                  : {}
                              }
                            >
                              {parseFloat(item.PE?.Theta ?? 0).toFixed(2)}
                            </td>
                          )}

                          {selectedGreeks.includes('DELTA') && (
                            <td className="table-right"
                              style={
                                item.strikePrice.toString().endsWith("00")
                                  ? { backgroundColor: "#ffffae" }
                                  : {}
                              }>
                              {parseFloat(item.PE?.Delta ?? 0).toFixed(2)}
                            </td>
                          )}
                        </tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={sideColumns * 2 + 1}>No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Playback controls: pinned to the bottom of the window so they stay visible while
                scrolling (the spacer keeps the end of the page from hiding behind them). */}
            <div style={{ height: 64 }} aria-hidden="true" />
            <div className="table-controls bt-sticky-controls">
              {/* Left side buttons (Decrease Time) */}
              <div className="interval-buttons">
                {/* <button onClick={() => handleIntervalChange(1000, "backward")}>1 Sec</button>
                <button onClick={() => handleIntervalChange(2000, "backward")}>2 Sec</button>
                <button onClick={() => handleIntervalChange(5000, "backward")}>5 Sec</button>
                <button onClick={() => handleIntervalChange(10000, "backward")}>10 Sec</button> */}

                  <button className={stepSide === 'backward' && playStepMs === 10000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(10000, 'backward')}>10 Sec</button>
                <button className={stepSide === 'backward' && playStepMs === 30000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(30000, 'backward')}>30 Sec</button>
                <button className={stepSide === 'backward' && playStepMs === 60000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(60000, 'backward')}>1 Min</button>
                <button className={stepSide === 'backward' && playStepMs === 180000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(180000, 'backward')}>3 Min</button>
                <button
                  className={playDirection === 'backward' ? 'bt-play-active' : ''}
                  onClick={() => startPlayback('backward')}
                  title={`Play backward: ${stepLabel(playStepMs)} back every 2 seconds`}
                >
                  {playDirection === 'backward' ? '⏸ Pause' : '◀ Play'}
                </button>
              </div>

              {/* Center Play/Pause */}
              <div className="play-button">
                <button onClick={togglePlayback}>
                  {isPlaying ? "⏸ Pause" : "▶ Play"}
                </button>
              </div>

              {/* Right side buttons (Increase Time) */}
              <div className="interval-buttons">
                <button
                  className={playDirection === 'forward' ? 'bt-play-active' : ''}
                  onClick={() => startPlayback('forward')}
                  title={`Play forward: ${stepLabel(playStepMs)} ahead every 2 seconds`}
                >
                  {playDirection === 'forward' ? '⏸ Pause' : 'Play ▶'}
                </button>
                <button className={stepSide === 'forward' && playStepMs === 10000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(10000, 'forward')}>10 Sec</button>
                <button className={stepSide === 'forward' && playStepMs === 30000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(30000, 'forward')}>30 Sec</button>
                <button className={stepSide === 'forward' && playStepMs === 60000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(60000, 'forward')}>1 Min</button>
                <button className={stepSide === 'forward' && playStepMs === 180000 ? 'bt-step-active' : ''} onClick={() => selectPlayStep(180000, 'forward')}>3 Min</button>
              </div>


            </div>


          </div>
        </div>
      </section>
      <section className="volume-section ">
        <div className="container">
          <div className="volume-section-inner">
            <ul className="list-unstyled d-flex align-items-center justify-content-center">
              <li><a href="#">TV PE: 50%</a></li>
              <li> <h3>Delta PE: 3.70 <br />LTP PE: 31%</h3></li>
              <li> <h3>Delta PE: 3.70 <br />LTP PE: 31%</h3></li>
              <li><a href="#" className="text-danger">TV PE: 50%</a></li>
            </ul>
          </div>
          <div className="crr-volume-inner">
            <div className="row justify-content-center">
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>CPR Volume: {countCprVolume(data).pcrOi}</h3>
                  <p>Total Volume CE:{formatNumber2(countCprVolume(data).totalCeOI)}</p>
                  <p>Total Volume PE:{formatNumber2(countCprVolume(data).totalCeOI)}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>PCR OI: {countPcrOI(data).pcrOi}</h3>
                  <p>Total OI CE: {formatNumber2(countPcrOI(data).totalCeOI)}</p>
                  <p>Total OI PE:{formatNumber2(countPcrOI(data).totalPeOI)}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="crr-volume-content">
                  <h3>PCR OI Change:{countPcrOIchg(data).pcrOi}</h3>
                  <p>Total OI change CE: {formatNumber2(countPcrOIchg(data).totalCeOI)}</p>
                  <p>Total OI change PE: {formatNumber2(countPcrOIchg(data).totalPeOI)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="des-section">
        <div className="container">
          <div className="dessection-inner">
            <p>PCR is the ratio of the PUT OI divided by CALL OI. PCR is used by traders to gauge the overall sentiment of the market. If the PCR is high, it means that the market is bullish and if the PCR is low, it means that the market is bearish . PCR between 0.8 and 1.2 is considered normal. If the PCR is below 0.8, it means that the market is bearish and if the PCR is above 1.2, it means that the market is bullish. PCR of OI and OI Change is directly proportional to CPR of volume.</p>
          </div>
        </div>
      </section>

      {/* ======== Setting popup ======== */}
      <div className="modal fade" id="settingModel" tabIndex={-1} aria-labelledby="settingModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="settingModelLabel">Setting</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="setting-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/light-mode.svg" alt="icon" /></span>
                      <span>Light Mode</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input light-mode" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/highlight.svg" alt="icon" /></span>
                      <span>ITM &amp; OTM Highlight</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input itm-otm-highlight" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/screen-desktop.svg" alt="icon" /></span>
                      <span>Keep Screen Awake</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input keep-screen-awake" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/price-tag.svg" alt="icon" /></span>
                      <span>Show Future Price</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input show-future-price" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/top-so.svg" alt="icon" /></span>
                      <span>High Third Highest</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input high-third-highest" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/number-sign.svg" alt="icon" /></span>
                      <span>Show Numbers</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input show-numbers" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/table-column.svg" alt="icon" /></span>
                      <span>Reverse Table</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input reverse-table" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/percentage.svg" alt="icon" /></span>
                      <span>Prefer Percentage</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input prefer-percentage" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/show-qt.svg" alt="icon" /></span>
                      <span>Show QT in Lots</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input show-qt" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/trash.svg" alt="icon" /></span>
                      <span>Clear Data Cache</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input clear-data-cache" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Search Nifty popup ======== */}
      <div className="modal fade" id="searchNiftyModel" tabIndex={-1} aria-labelledby="searchNiftyModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-4">
              <form className="search-field-container">
                <div className="form-input">
                  <input type="search" name="Search Nifty" className="form-control" placeholder="Search Stock/Index" />
                </div>
              </form>
              <div className="nifty-index-list">
                <ul className="list-unstyled">
                  <li><button>Banknifty</button></li>
                  <li><button>Finnifty</button></li>
                  <li><button>Nifty_Mid_Select</button></li>
                  <li><button>Nifty</button></li>
                  <li><button>Naturalgas</button></li>
                  <li><button>Crudeoil</button></li>
                  <li><button>Usdinr</button></li>
                  <li><button>Sensex</button></li>
                  <li><button>bankex</button></li>
                  <li><button>Aartiind</button></li>
                  <li><button>Sensex</button></li>
                  <li><button>bankex</button></li>
                  <li><button>Aartiind</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== OI Charts popup (selected date, full session 09:15–15:30 whatever the selected time) ======== */}
      <div
        className="modal fade "
        id="oichartModel"
        tabIndex={-1}
        aria-labelledby="oichartModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue oi-charts-dark">
            <div className="modal-header">
              <h5 className="modal-title" id="oichartModelLabel">
                OI Charts{appliedQuery ? ` · ${appliedQuery.date}` : ''}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <OIChartsPanel
              instrumentIdentifier={appliedQuery ? oiChartsInstrument : null}
              date={appliedQuery?.date}
            />
          </div>
        </div>
      </div>
      {/* ======== Spot Chart / Future Price Graph popups (selected date, full session whatever the selected time) ======== */}
      <SpotChartModal
        symbol={appliedQuery?.symbol || localStorage.getItem("stock") || "NIFTY"}
        levels={spotChartLevels}
        date={appliedQuery?.date}
      />
      <FuturePriceGraphModal
        symbol={appliedQuery?.symbol}
        date={appliedQuery?.date}
        onLatest={setLatestFuture}
      />
      {/* ======== OI Percentage Graph-1 popup ======== */}
      <div
        className="modal fade"
        id="oipercentage1Model"
        tabIndex={-1}
        aria-labelledby="oioipercentage1ModelLabel"
        aria-hidden="true "
      >
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content" style={{ background: "#131313", border: "1px solid #262626", borderRadius: 8 }}>
            <OIPercentageGraph
              data={oiPctGraph.points || []}
              sessionStart={oiPctGraph.sessionStart}
              sessionEnd={oiPctGraph.sessionEnd}
              loading={oiPctGraphLoading}
              label={oiPercentageGraphCE || oiPercentageGraphPE}
              markerTime={selectedTime}
            />
          </div>
        </div>
      </div>
      {/* ======== Combined Price Chart IV- popup ======== */}
      <div className="modal fade " id="CombinedpriceModel" tabIndex={-1} aria-labelledby="CombinedpriceModelLabel" aria-hidden="true ">
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="CombinedpriceModel">Combined Price Chart</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="oi-chart-btn-inner">
                <ul className="list-unstyled d-flex justify-content-center align-items-center">
                  <li> <button> <span className="oi-chart-lite-blue" />Combined Price</button></li>
                  <li> <button> <span className="oi-chart-green" />CE Price</button></li>
                  <li> <button> <span className="oi-chart-red" />PE Price</button></li>
                </ul>
              </div>
            </div>
            <div className="tab-content" id="pills-tabContent">
              <div className="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                <div className="ai-chart-img">
                  <img src="img/combined-price-chart-iv.svg" alt="chart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Combined Price Chart IV-1 popup ======== */}
      <div className="modal fade " id="futureBuildupModel" tabIndex={-1} aria-labelledby="futureBuildupModelLabel" aria-hidden="true ">
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="futureBuildupModel">Future Buildup: Nifty</h5>
              <button type="button" className="info-table" data-bs-dismiss="modal" data-bs-target="#futureBuildupinfoModel"><i style={{ fontSize: '24px' }} className="fa fa-info-circle" /></button>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="oi-chart-btn-inner">
                <ul className="list-unstyled d-flex justify-content-center align-items-center">
                  <li> <button> <span className="oi-chart-lite-blue" />Future Price</button></li>
                  <li> <button> <span className="oi-chart-wine" />Future OI Price</button></li>
                </ul>
              </div>
            </div>
            <div className="tab-content" id="pills-tabContent">
              <div className="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab">
                <div className="ai-chart-img">
                  <img src="img/combined-price-chart-iv-1.svg" alt="chart" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== info-table popup ======== */}
      <div className="modal fade " id="futureBuildupinfoModel" tabIndex={-1} aria-labelledby="futureBuildupinfoModelLabel" aria-hidden="true ">
        <div className="modal-dialog ai-chart modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <div className="oi-chart-btn-inner  ">
                <table className="info-table  w-100 ">
                  <tbody><tr className="info-table-row ">
                    <th className="info-table-heading">Price</th>
                    <th className="info-table-heading">OI or OI CHNG</th>
                    <th className="info-table-heading">Result</th>
                    <th className="info-table-heading">Meaning</th>
                    <th className="info-table-heading">Option Trade</th>
                  </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                    <tr className="info-table-row-1">
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Up</td>
                      <td className="info-table-data">Long Buildup</td>
                      <td className="info-table-data">Strong Bullish</td>
                      <td className="info-table-data">CE Buy</td>
                    </tr>
                  </tbody></table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== volume-chart popup ======== */}
      <div className="modal fade " id="volumeChartModel" tabIndex={-1} aria-labelledby="volumeChartModelLabel" aria-hidden="true ">
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                {/* <h3><strong>Possible Reversal For</strong> : 21500</h3> */}
                <h3><strong>Possible Reversal For</strong> : {selectedStrikePrice}</h3>
                <h4><strong>Resistance:</strong> {(resistance).toFixed(2)} <button className="volume-copy-btn" data-bs-target="#valueCopyModel" data-bs-dismiss="modal"><i className="fa fa-clone" aria-hidden="true" /></button> </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade " id="volumeChartModel12" tabIndex={-1} aria-labelledby="volumeChartModel12Label" aria-hidden="true ">
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                {/* <h3><strong>Possible Reversal For</strong> : 21500</h3> */}
                <h3><strong>Possible Reversal For</strong> : {selectedStrikePrice}</h3>
                <h4><strong>Support:</strong> {(support).toFixed(2)} <button className="volume-copy-btn" data-bs-target="#valueCopyModel" data-bs-dismiss="modal"><i className="fa fa-clone" aria-hidden="true" /></button> </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== volume-chart-1 popup ======== */}
      <div className="modal fade valueCopy" id="valueCopyModel" tabIndex={-1} aria-labelledby="valueCopyModelLabel" aria-hidden="true ">
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner volume-chart-1  ">
                <h3>Value Copied to clipboard</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== LTP Calculator popup ======== */}
      <div className="modal fade " id="LPTcalculatorModel" tabIndex={-1} aria-labelledby="LPTcalculatorModelLabel" aria-hidden="true ">
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="LPTcalculatorModelLabel">LTP calculator 22000 CE</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <input type="text" placeholder="Target spot price" className="ltp-calculator-field" />
              <div className="volume-chart-inner ltp-calculator-content ">
                <h3><strong>Buy Price:</strong> - 3216.09</h3>
                <h4><strong>SL: </strong> - 3219.09</h4>
                <h5><strong>Target: </strong> - 6527.25</h5>
                <p>This is a rough estimate, calculation is based on delta.
                  Please use your own judgement and do not trade based on
                  this alone.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== free-user popup ======== */}
      <div className="modal fade " id="freeUserModel" tabIndex={-1} aria-labelledby="freeUserModelLabel" aria-hidden="true ">
        <div className="modal-dialog  modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="freeUserModelLabel">Upgrade to Pro Plan for <br />
                faster data update</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner  ">
                <div className="free-user-content">
                  <p>As a free user, you will get delayed data by 3 minutes.</p>
                  <p>Upgrade to Pro Plan to get faster data updates and <br />
                    access to all features.</p>
                </div>
                <div className="free-user-check">
                  <h2><i style={{ color: '#25D366' }} className="fa fa-check-circle-o" aria-hidden="true"> </i> Fast data updates</h2>
                  <h2><i style={{ color: '#25D366' }} className="fa fa-check-circle-o" aria-hidden="true"> </i> Ads free experience</h2>
                  <h2><i style={{ color: '#25D366' }} className="fa fa-check-circle-o" aria-hidden="true"> </i>    Access to all features</h2>
                  <h2><i style={{ color: '#25D366' }} className="fa fa-check-circle-o" aria-hidden="true"> </i> Access to all future features</h2>
                  <h2><i style={{ color: '#25D366' }} className="fa fa-check-circle-o" aria-hidden="true"> </i> Premium support</h2>
                </div>
                <div className="free-user-btn-content d-flex justify-content-center align-items-center">
                  <button className="free-user-btn me-3">Continue as free user</button>
                  <button className="free-user-btn btn-red">Subscribe to Pro Plan</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Search Nifty popup ======== */}
      <div className="modal fade" id="searchNiftyModel" tabIndex={-1} aria-labelledby="searchNiftyModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-4">
              <form className="search-field-container">
                <div className="form-input">
                  <input type="search" name="Search Nifty" className="form-control" placeholder="Search Stock/Index" />
                </div>
              </form>
              <div className="nifty-index-list">
                <ul className="list-unstyled">
                  <li><button>Banknifty</button></li>
                  <li><button>Finnifty</button></li>
                  <li><button>Nifty_Mid_Select</button></li>
                  <li><button>Nifty</button></li>
                  <li><button>Naturalgas</button></li>
                  <li><button>Crudeoil</button></li>
                  <li><button>Usdinr</button></li>
                  <li><button>Sensex</button></li>
                  <li><button>bankex</button></li>
                  <li><button>Aartiind</button></li>
                  <li><button>Sensex</button></li>
                  <li><button>bankex</button></li>
                  <li><button>Aartiind</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Update Profile popup ======== */}
      <div className="modal fade" id="updateModel" tabIndex={-1} aria-labelledby="updateModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">Update Profile</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#"><img src="img/header-logo.svg" alt="logo" /></a>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <input type="text" name="Name" className="form-control" placeholder="Name" />
                  </div>
                  <div className="form-input mb-3">
                    <input type="text" name="State" className="form-control" placeholder="State" />
                  </div>
                  <div className="form-input mb-3">
                    <input type="text" name="District" className="form-control" placeholder="District" />
                  </div>
                  <div className="form-input mb-3">
                    <input type="text" name="City" className="form-control" placeholder="City" />
                  </div>
                  <div className="form-input mb-3">
                    <input type="text" name="Phone" className="form-control" placeholder="Phone" />
                  </div>
                  <div className="update-btn-outer">
                    <button type="button" className="site-btn btn-blue-o" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                    <button type="submit" className="update-btn site-btn btn-blue">Update</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Delete Account Warning popup ======== */}
      <div className="modal fade" id="deleteACModel" tabIndex={-1} aria-labelledby="deleteACModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-green">
            <div className="modal-body py-4 px-5 text-center">
              <h4>Warning !!</h4>
              <p className="desc my-2">Are you sure you want to delete your account?</p>
              <p className="desc mb-2">This action cannot be done.</p>
              <p className="desc mb-2">All your data will be deleted.</p>
              <div className="donate-btn-grp text-center py-4">
                <button className="site-btn btn-blue delete-btn" data-bs-toggle="modal" data-bs-target="#deleteACModel">Cancel</button>
                <button className="site-btn btn-red delete-btn">Delete Account</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Notification popup ======== */}
      <div className="modal fade" id="notificationModel" tabIndex={-1} aria-labelledby="notificationModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="notificationModelLabel">Notification Setting</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="notification-functionalty-outer popup-list">
                <ul className="list-unstyled">
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/stock-ticker.svg" alt="icon" /></span>
                      <span>Stocks</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input stocks" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/data-collection.svg" alt="icon" /></span>
                      <span>Indices</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input indices" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/currency.svg" alt="icon" /></span>
                      <span>Currency</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input currency" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="popup-list-title">
                      <span><img src="img/stock_mcx.svg" alt="icon" /></span>
                      <span>MCX</span>
                    </div>
                    <div className="toggle-status">
                      <div className="form-check form-switch">
                        <input className="form-check-input mcx" type="checkbox" id="flexSwitchCheckChecked" defaultChecked />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Disclaimer popup ======== */}
      <div className="modal fade" id="disclaimerModel" tabIndex={-1} aria-labelledby="disclaimerModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <h5 className="modal-title" id="disclaimerModelLabel">Disclaimer</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              <div className="disclaimer-functionalty-outer">
                <p>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. It was popularised in the 1960s with the release of Letraset sheets containing Lorem Ipsum passages, and more recently with desktop publishing software like Aldus PageMaker including versions of Lorem Ipsum<br />Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged. </p>
                <div className="modal-footer mt-3">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                  <button type="button" className="btn btn-primary site-btn" data-bs-dismiss="modal">Accept &amp; Continue</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Login popup ======== */}
      <div className="modal fade" id="loginModel" tabIndex={-1} aria-labelledby="loginModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#"><img src="img/header-logo.svg" alt="logo" /></a>
              </div>
              <div className="login-title text-center my-3">
                <h4>Login</h4>
                <h5>Welcome to Option Trading Wala <br /> Please Login to Continue</h5>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput1" className="form-label">Email / Phone No</label>
                    <input type="text" name="Email or Phone No" className="form-control" placeholder="Enter Email or Phone No." />
                  </div>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput2" className="form-label">Password</label>
                    <input type="password" name="Password" className="form-control" placeholder="Enter Password" />
                  </div>
                  <div className="otp-outer text-center">
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner my-2">
                      <input type="text" name="otp-code-1" maxLength={1} />
                      <input type="text" name="otp-code-2" maxLength={1} />
                      <input type="text" name="otp-code-3" maxLength={1} />
                      <input type="text" name="otp-code-4" maxLength={1} />
                      <input type="text" name="otp-code-5" maxLength={1} />
                      <input type="text" name="otp-code-6" maxLength={1} />
                    </div>
                    <p><a href="#" className="text-success">Resend OTP In</a><span className="otp-time ms-2">00:28</span></p>
                  </div>
                  <button type="submit" className="submit-btn">Login</button>
                </form>
                <div className="signup-and-forgot-pswd d-flex justify-content-between mt-2">
                  <a href="#" className="text-danger">SignUp</a>
                  <a href="#">Forget Password?</a>
                </div>
                <div className="privacy-policy-and-terms-&-service text-center mt-4">
                  <p>By Clicking on login you agree to <br /><a href="#">Privacy Policy</a> &amp; <a href="#">Terms &amp; Service</a></p>
                </div>
                <div className="google-sign-in text-center">
                  <button><img src="img/google-logo.svg" alt="google-logo" />Sign in with Google</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Strike price popup ======== */}
      {/* ======== Strike price popup ======== */}
      <div
        className="modal fade"
        id="strikePriceModel"
        tabIndex={-1}
        aria-labelledby="strikePriceModelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner">
                <h5>Elastic Ends Of Strike Price</h5>
                <br />
                <h2>
                  <strong>{selectedStrikePrice}</strong>
                </h2>
                <div className="modal-info-section">
                  <ul
                    className="nav nav-tabs justify-content-center"
                    id="myTab"
                    role="tablist"
                  >
                    <li className="nav-item">
                      <a
                        className="nav-link active"
                        id="home-tab"
                        data-bs-toggle="tab"
                        href="#home"
                        role="tab"
                        aria-controls="home"
                        aria-selected="true"
                      >
                        SPOT
                      </a>
                    </li>
                    <li className="nav-item">
                      <a
                        className="nav-link"
                        id="profile-tab"
                        data-bs-toggle="tab"
                        href="#profile"
                        role="tab"
                        aria-controls="profile"
                        aria-selected="false"
                      >
                        FUTURE
                      </a>
                    </li>

                  </ul>
                </div>

                <div className="tab-content" id="myTabContent">
                  <div
                    className="tab-pane fade show active"
                    id="home"
                    role="tabpanel"
                    aria-labelledby="home-tab"
                  >
                    <div className="modal-info-section">
                      <h5>Average Elastic Ends(9:30 AM)</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        {resistance2.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{" "}
                        {support2.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                    <div className="modal-info-section">
                      <h5>Current Elastic Ends</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        {resistance.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{support.toFixed(2)}
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                  </div>

                  <div
                    className="tab-pane fade"
                    id="profile"
                    role="tabpanel"
                    aria-labelledby="profile-tab"
                  >
                    <div className="modal-info-section">
                      <h5>Average Elastic Ends(9:30 AM)</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        24573.55
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span>{" "}
                        24941.8083
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                    <div className="modal-info-section">
                      <h5>Current Elastic Ends</h5>
                      <h2>
                        <span className="text-danger">Resistance:</span>{" "}
                        25065.35
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                      <h2>
                        <span className="text-success">Support:</span> 25007.11
                        <button
                          className="volume-copy-btn"
                          data-bs-toggle="modal"
                          data-bs-target="#valueCopyModel"
                        >
                          <i className="fa fa-clone" aria-hidden="true"></i>
                        </button>
                      </h2>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======== Expiry dates popup ======== */}

      {/* time picker modal */}
      <div className="modal fade" id="timePickerModel" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered ">
          <div className="modal-content">
            <div className="modal-body p-0">
              <div className="time-picker">
                <div className="time-left">
                  <div className="time-show">
                    <div className="time-selector" onClick={handleHourClick}>
                      <span>{selectedTimePicked.hour}</span>
                      {isHourPickerVisible && (
                        <div className="hour-picker">
                          {Array.from({ length: 12 }).map((_, i) => (
                            <button key={i} onClick={() => handleNumberClick(i === 0 ? 12 : i)}>
                              {i === 0 ? 12 : i}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <span>:</span>
                    <div className="time-selector" onClick={handleMinuteClick}>
                      <span>{selectedTimePicked.minute.toString().padStart(2, "0")}</span>
                      {isMinutePickerVisible && (
                        <div className="minute-picker">
                          {Array.from({ length: 60 }).map((_, i) => (
                            <button key={i} onClick={() => handleMinuteSelection(i)}>
                              {i.toString().padStart(2, "0")}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <span> {selectedTimePicked.period}</span>
                  </div>
                  <div className="am-pm-toggle buttons justify-content-start">
                    <button onClick={() => togglePeriod("AM")} className={selectedTimePicked.period === "AM" ? "selected" : ""}>
                      AM
                    </button>
                    <button onClick={() => togglePeriod("PM")} className={selectedTimePicked.period === "PM" ? "selected" : ""}>
                      PM
                    </button>
                  </div>
                </div>
                <div className="time-right">
                  <div className="clock-face">
                    <svg viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="45" fill="#ddd" stroke="black" strokeWidth="2" />
                      {Array.from({ length: 12 }).map((_, i) => {
                        const angle = ((i * 30) - 90) * Math.PI / 180;
                        return (
                          <text
                            style={{ cursor: "pointer" }}
                            key={i}
                            x={50 + 35 * Math.cos(angle)}
                            y={50 + 35 * Math.sin(angle) + 4}
                            textAnchor="middle"
                            fontSize="8"
                            fill="black"
                            onClick={() => handleNumberClick(i === 0 ? 12 : i)}
                          >
                            {i === 0 ? 12 : i}
                          </text>
                        );
                      })}
                      <line
                        x1="50"
                        y1="50"
                        x2={50 + 30 * Math.cos((clockHandAngles.hour - 90) * Math.PI / 180)}
                        y2={50 + 30 * Math.sin((clockHandAngles.hour - 90) * Math.PI / 180)}
                        stroke="black"
                        strokeWidth="3"
                      />
                      <line
                        x1="50"
                        y1="50"
                        x2={50 + 40 * Math.cos((clockHandAngles.minute - 90) * Math.PI / 180)}
                        y2={50 + 40 * Math.sin((clockHandAngles.minute - 90) * Math.PI / 180)}
                        stroke="black"
                        strokeWidth="2"
                      />
                      <circle cx="50" cy="50" r="3" fill="black" />
                    </svg>
                  </div>
                  <div className="buttons">
                    <button data-bs-dismiss="modal">Cancel</button>
                    <button className='selected' data-bs-dismiss="modal" onClick={handleTimeChange} value={selectedTime}>Confirm</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end modal */}
      {/* Select Date Modal */}
      {/*====== Call vs Put OI===== */}
      <div
        className="modal fade"
        id="callVsPutOI"
        tabIndex={-1}
        aria-labelledby="callVsPutOILabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <div className="volume-chart-inner">

                <div className="row mb-6">
                  <div className="col">
                    <label htmlFor="rangeStart" className="form-label">Range Start</label>
                    <select
                      id="rangeStart"
                      className="form-select"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                    >
                      {rangeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col">
                    <label htmlFor="rangeEnd" className="form-label">Range End</label>
                    <select
                      id="rangeEnd"
                      className="form-select"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                    >
                      {rangeOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <h5 className="text-center">Change in Open Interest(COI)</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataCallPutOIChange} barCategoryGap={30}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(val) => `${val}L`} />
                      <Tooltip formatter={(value) => `${value}L`} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={30}>
                        {dataCallPutOIChange.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.name === "CALL" ? "red" : "green"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>



                </div>
                <div className="mt-4">
                  <h5 className="text-center"> Open Interest(OI)</h5>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataCallPutOI} barCategoryGap={30}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(val) => `${val}L`} />
                      <Tooltip formatter={(value) => `${value}L`} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={30}>
                        {dataCallPutOI.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.name === "CALL" ? "red" : "green"} // Red for CALL, Green for PUT
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
      {/* end code */}
      {/*Top 5 openinterestchange  */}
      <div
        className="modal fade"
        id="top5openinterestchnage"
        tabIndex={-1}
        aria-labelledby="top5openinterestchnageLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <h5 className="modal-title text-center mb-3" id="top5openinterestchnageLabel">
                Top 5 Open Interest Change
              </h5>
              <ul className="list-group">
                {top5OI.map((item, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                    <span>Strike Price: {item.strikePrice}</span>
                    <span>{item.type} - Change: {item.change}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </div>
      {/* end code top 5 */}
      {/*call and put writing  */}
      <div
        className="modal fade"
        id="callAndPutWriting"
        tabIndex={-1}
        aria-labelledby="callAndPutWritingLabel"

      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body">
              <h5 className="modal-title text-center mb-3" id="callAndPutWritingLabel">
                Call Writing And Put Writing
              </h5>
              <h5 style={{
                color: cumulativeCallOIChange > cumulativePutOIChange
                  ? "red"
                  : cumulativePutOIChange > cumulativeCallOIChange
                    ? "green"
                    : "black"
              }}>
                {cumulativeCallOIChange > cumulativePutOIChange
                  ? "1.TOTAL: CW > PW"
                  : cumulativePutOIChange > cumulativeCallOIChange
                    ? "1.TOTAL: PW > CW"
                    : "1.TOTAL: CW = PW"}
              </h5>

              <h5 style={{
                color: totalPE_1 > totalCE_1
                  ? "green"
                  : totalCE_1 > totalPE_1
                    ? "red"
                    : "black"
              }}><br />
                2.NEAREST STRIKE: {totalPE_1 > totalCE_1
                  ? "PW > CW"
                  : totalCE_1 > totalPE_1
                    ? "CW > PW"
                    : "PW and CE = CW"}
              </h5>

              <h5 style={{
                color: totalPE_2 > totalCE_2
                  ? "green"
                  : totalCE_2 > totalPE_2
                    ? "red"
                    : "black"
              }}><br />
                3. TWO NEAREST STRIKE: {totalPE_2 > totalCE_2
                  ? "PW > CW"
                  : totalCE_2 > totalPE_2
                    ? "CW > PW"
                    : "PW and CE = CW"}
              </h5>

            </div>

          </div>
        </div>
      </div>

      {/* ======== Signup popup ======== */}
      <div className="modal fade" id="signupModel" tabIndex={-1} aria-labelledby="signupModelLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content modal-bg-light-blue">
            <div className="modal-body py-4 px-5">
              <div className="header-logo text-center">
                <a href="#"><img src="img/header-logo.svg" alt="logo" /></a>
              </div>
              <div className="login-title text-center my-3">
                <h4>Register</h4>
                <h5>Welcome to Option Trading Wala <br /> Please Register to Continue</h5>
              </div>
              <div className="login-form-outer">
                <form>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput1" className="form-label">Name</label>
                    <input type="text" name="Name" className="form-control" placeholder="Enter Name" />
                  </div>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput2" className="form-label">Email</label>
                    <input type="email" name="Email" className="form-control" placeholder="Enter Email" />
                  </div>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput3" className="form-label">Phone No.</label>
                    <input type="tel" name="phone no." className="form-control" placeholder="Enter Phone No." />
                  </div>
                  <div className="form-input mb-3">
                    <label htmlFor="exampleFormControlInput4" className="form-label">Password</label>
                    <input type="password" name="Password" className="form-control" placeholder="Enter Password" />
                  </div>
                  <div className="otp-outer text-center">
                    <p>Enter your OTP</p>
                    <div className="otp-code-inner my-2">
                      <input type="text" name="otp-code-1" maxLength={1} />
                      <input type="text" name="otp-code-2" maxLength={1} />
                      <input type="text" name="otp-code-3" maxLength={1} />
                      <input type="text" name="otp-code-4" maxLength={1} />
                      <input type="text" name="otp-code-5" maxLength={1} />
                      <input type="text" name="otp-code-6" maxLength={1} />
                    </div>
                    <p><a href="#" className="text-success">Resend OTP In</a><span className="otp-time ms-2">00:28</span></p>
                  </div>
                  <button type="submit" className="submit-btn">Login</button>
                </form>
                <div className="signup-and-forgot-pswd d-flex justify-content-center mt-2">
                  <p>Already have an Account? <a href="#" className="text-danger">Login</a></p>
                </div>
                <div className="privacy-policy-and-terms-&-service text-center mt-4">
                  <p>By Clicking on login you agree to <br /><a href="#">Privacy Policy</a> &amp; <a href="#">Terms &amp; Service</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
}

export default BackTester